import { askText, askConfirm, bindDialog } from './dialog';
import type { WebKilnApiClient } from './api-client';
import type {
  AssetMetadata,
  AuditEvent,
  Automation,
  CloudSite,
  FormDefinition,
  FormField,
  FormFieldType,
  FormSubmission,
  Session,
  Workspace,
  WorkspaceNotification,
} from './contracts';
import { navigate, parseRoute } from './app-router';
import { renderWorkspaceSetup } from './workspace-setup';
import type { LocalProjectStorage } from '../storage/project-storage';
import { createMigrationBackup, previewLocalProject } from './local-import';
import { findDuplicateAssetIds, extractFolderHierarchy, normalizeFolderPath, computeAssetContentHash } from '../assets/asset-storage';
import { createProjectBackup, createStaticExport } from '../portability/export';
import { createTemplateProject, templateById, WEBKILN_TEMPLATES } from './template-catalog';
import { FORM_FIELD_TYPES, validateFormFields } from './form-builder';
import {
  AUTOMATION_ACTIONS,
  AUTOMATION_TRIGGERS,
  validateAutomationGraph,
} from './automation-builder';

const esc = (value: string) =>
  value.replace(
    /[&<>"']/g,
    (character) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character] ??
      character,
  );
const sections = [
  'overview',
  'analytics',
  'websites',
  'collections',
  'automations',
  'templates',
  'domains',
  'forms',
  'assets',
  'billing',
  'team',
  'account',
] as const;
type Section = (typeof sections)[number];

export type WebsiteFilter = 'all' | 'drafts' | 'published' | 'archived';
export type WebsiteSort = 'recent' | 'name' | 'status';

export function filterAndSortWebsites(
  sites: CloudSite[],
  query = '',
  filter: WebsiteFilter = 'all',
  sort: WebsiteSort = 'recent',
): CloudSite[] {
  const normalizedQuery = query.trim().toLowerCase();
  return sites
    .filter((site) => {
      const matchesQuery =
        !normalizedQuery ||
        [site.name, site.slug, site.customDomain ?? ''].some((value) =>
          value.toLowerCase().includes(normalizedQuery),
        );
      const matchesFilter =
        filter === 'all' ||
        (filter === 'archived' && site.status === 'archived') ||
        (filter === 'drafts' && site.status !== 'archived' && !site.published) ||
        (filter === 'published' && site.status !== 'archived' && site.published === true);
      return matchesQuery && matchesFilter;
    })
    .sort((left, right) => {
      if (sort === 'name') return left.name.localeCompare(right.name);
      if (sort === 'status') {
        const statusOrder = (site: CloudSite) =>
          site.status === 'archived' ? 2 : site.published ? 1 : 0;
        return statusOrder(left) - statusOrder(right) || left.name.localeCompare(right.name);
      }
      return Date.parse(right.updatedAt || '') - Date.parse(left.updatedAt || '');
    });
}

export async function renderDashboard(
  cloud: WebKilnApiClient,
  storage: LocalProjectStorage,
  session: Session,
  initialSection = 'overview',
  initialWorkspaceId = '',
): Promise<void> {
  const navIcon = (section: string) =>
    ({
      overview: '◌',
      analytics: '⌁',
      websites: '◈',
      collections: '{}',
      automations: '↯',
      templates: '✦',
      domains: '⌁',
      forms: '▤',
      assets: '▧',
      billing: '◒',
      team: '◎',
      account: '⚙',
    })[section] ?? '•';
  const sectionLabel = (section: string) => section.charAt(0).toUpperCase() + section.slice(1);
  document.body.innerHTML = `<main class="cloud-app customer-dashboard"><header class="cloud-topbar dashboard-topbar"><a class="cloud-brand" href="/" aria-label="WebKiln home"><span class="brand-mark">W</span><span>WEBKILN</span></a><div class="dashboard-context"><span class="connection-dot"></span><span>Cloud workspace</span></div><div class="account-menu"><details><summary>${esc(session.user.displayName)}</summary><div class="account-popover"><strong>${esc(session.user.displayName)}</strong><small>${esc(session.user.email)}</small><button type="button" data-nav="account">Account settings</button><button type="button" data-signout>Sign out</button></div></details></div></header><div class="dashboard-frame"><aside class="dashboard-sidebar"><p class="sidebar-label">Workspace</p><nav aria-label="Dashboard navigation">${sections.map((section) => `<button type="button" data-nav="${section}" class="dashboard-nav ${section === 'overview' ? 'active' : ''}"><span>${navIcon(section)}</span>${sectionLabel(section)}</button>`).join('')}</nav><div class="sidebar-footer"><button type="button" data-local-editor>Open local editor</button><small>WebKiln customer cloud</small></div></aside><section class="dashboard-main"><div data-dashboard-view></div><div class="cloud-toast" data-dashboard-message role="status" aria-live="polite"></div></section></div></main>`;
  let workspaces: Workspace[] = [];
  let selectedWorkspaceId = initialWorkspaceId;
  let sites: CloudSite[] = [];
  let renderVersion = 0;
  let duplicateIds = new Set<string>();
  const view = document.querySelector<HTMLElement>('[data-dashboard-view]')!;
  const message = document.querySelector<HTMLElement>('[data-dashboard-message]')!;
  const say = (text: string) => {
    message.textContent = text;
    window.setTimeout(() => {
      if (message.textContent === text) message.textContent = '';
    }, 3500);
  };
  const loadSites = async () => {
    if (!selectedWorkspaceId) return;
    const version = ++renderVersion;
    const section = (view.dataset.section as Section) || 'websites';
    view.innerHTML = loading('Loading websites');
    try {
      const loaded = await cloud.listSites(selectedWorkspaceId);
      if (version !== renderVersion) return;
      sites = loaded;
      renderSection(section);
    } catch (error) {
      if (version === renderVersion) view.innerHTML = errorState(error, section);
    }
  };
  const renderSection = (section: Section, updateHistory = true) => {
    renderVersion += 1;
    view.dataset.section = section;
    document
      .querySelectorAll<HTMLButtonElement>('.dashboard-sidebar [data-nav]')
      .forEach((button) => {
        const active = button.dataset.nav === section;
        button.classList.toggle('active', active);
        if (active) button.setAttribute('aria-current', 'page');
        else button.removeAttribute('aria-current');
      });
    if (updateHistory) {
      const query = new URLSearchParams({ section });
      if (selectedWorkspaceId) query.set('workspace', selectedWorkspaceId);
      const url = `/dashboard?${query}`;
      if (window.location.pathname + window.location.search !== url)
        history.pushState(null, '', url);
    }
    if (
      !selectedWorkspaceId &&
      [
        'overview',
        'analytics',
        'websites',
        'collections',
        'automations',
        'domains',
        'forms',
        'assets',
        'team',
      ].includes(section)
    ) {
      renderWorkspaceSetup(view, cloud, (workspace) => {
        workspaces.push(workspace);
        selectedWorkspaceId = workspace.id;
        sites = [];
        renderSection(section);
      });
      return;
    }

    document
      .querySelectorAll<HTMLButtonElement>('[data-nav]')
      .forEach((button) => button.classList.toggle('active', button.dataset.nav === section));
    if (section === 'overview') void renderOverview();
    else if (section === 'analytics') renderAnalytics();
    else if (section === 'websites') renderWebsites();
    else if (section === 'collections') void renderCollections();
    else if (section === 'automations') void renderAutomations();
    else if (section === 'forms') void renderForms();
    else if (section === 'assets') void renderAssets();
    else if (section === 'team') void renderTeam();
    else if (section === 'templates') renderTemplates();
    else renderUnavailable(section);
  };
  const renderOverview = async () => {
    let notifications: WorkspaceNotification[] = [];
    if (selectedWorkspaceId) {
      notifications = await cloud.listNotifications(selectedWorkspaceId).catch(() => []);
    }
    const unreadCount = notifications.filter((item) => !item.read).length;
    view.innerHTML = `<div class="customer-heading"><div><p class="eyebrow">Overview</p><h1>Good to see you, ${esc(session.user.displayName.split(' ')[0])}.</h1><p>Everything you need to keep your web presence moving.</p></div><div class="heading-actions"><button class="ghost-btn" data-create-blank-site>Create blank site</button><button class="primary-btn" data-create-site>New website</button></div></div><div class="dashboard-stats"><div><small>Websites</small><strong>${sites.length}</strong><span>In this workspace</span></div><div><small>Published</small><strong>${sites.filter((site) => site.published).length}</strong><span>Live experiences</span></div><div><small>Notifications</small><strong>${notifications.length}</strong><span>${unreadCount} unread</span></div></div>${
      notifications.length
        ? `<section class="dashboard-panel notifications-panel" data-notifications-panel><div class="panel-title"><div><p class="eyebrow">Workspace activity</p><h2>Notifications ${unreadCount ? `<span class="status-chip published">${unreadCount} new</span>` : ''}</h2></div></div><div class="notifications-list" data-notifications-list>${notifications
            .slice(0, 5)
            .map(
              (notif) =>
                `<div class="record-row notification-item ${notif.read ? 'read' : 'unread'}" data-notification-row="${esc(notif.id)}"><div style="flex:1"><strong>${esc(notif.title)}</strong><p style="margin:4px 0;font-size:13px;color:#adb3b5">${esc(notif.message)}</p><small>${new Date(notif.createdAt).toLocaleString()}</small></div><div class="website-actions">${!notif.read ? `<button class="ghost-btn" type="button" data-notification-read="${esc(notif.id)}">Mark read</button>` : ''}<button class="ghost-btn danger-action" type="button" data-notification-delete="${esc(notif.id)}">Dismiss</button></div></div>`,
            )
            .join('')}</div></section>`
        : ''
    }<section class="dashboard-panel overview-panel"><div class="panel-title"><div><p class="eyebrow">Recent websites</p><h2>Pick up where you left off</h2></div><button class="text-button" data-nav="websites">View all</button></div>${sites.slice(0, 3).map(siteCard).join('') || empty('No websites yet', 'Create a blank website or start from a template.')}</section><section class="dashboard-panel welcome-panel"><p class="eyebrow">A considered start</p><h2>Build with clarity.</h2><p>WebKiln keeps the details close and the interface calm, so your team can focus on the work visitors actually see.</p></section></div>`;
    bindCommon();
    view.querySelectorAll<HTMLButtonElement>('[data-notification-read]').forEach((button) =>
      button.addEventListener('click', async () => {
        try {
          await cloud.markNotificationRead(button.dataset.notificationRead ?? '');
          say('Notification marked as read.');
          void renderOverview();
        } catch (error) {
          say(error instanceof Error ? error.message : 'Could not update notification.');
        }
      }),
    );
    view.querySelectorAll<HTMLButtonElement>('[data-notification-delete]').forEach((button) =>
      button.addEventListener('click', async () => {
        try {
          await cloud.deleteNotification(button.dataset.notificationDelete ?? '');
          say('Notification dismissed.');
          void renderOverview();
        } catch (error) {
          say(error instanceof Error ? error.message : 'Could not dismiss notification.');
        }
      }),
    );
  };
  const renderAnalytics = () => {
    const totalPages = sites.reduce((sum, site) => sum + site.pageCount, 0);
    const revisions = sites.reduce((sum, site) => sum + site.currentRevision, 0);
    view.innerHTML = `<div class="customer-heading"><div><p class="eyebrow">Workspace / Analytics</p><h1>Understand what is ready.</h1><p>Calculated workspace signals are available now. Visitor analytics require a configured analytics provider and are not invented here.</p></div></div><div class="dashboard-stats"><div><small>Websites</small><strong>${sites.length}</strong><span>Calculated from workspace data</span></div><div><small>Published</small><strong>${sites.filter((site) => site.published).length}</strong><span>Calculated from deployment state</span></div><div><small>Pages</small><strong>${totalPages}</strong><span>Calculated from site metadata</span></div><div><small>Revisions</small><strong>${revisions}</strong><span>Calculated from saved revisions</span></div></div><section class="dashboard-panel analytics-panel"><div class="panel-title"><div><p class="eyebrow">Production measurements</p><h2>Analytics provider not connected</h2></div><span class="status-chip">Unavailable</span></div><p>WebKiln does not currently collect page views, visitors, conversion events, referrers, or performance telemetry. No external analytics script or paid observability service is enabled.</p><div class="analytics-unavailable-grid"><div><strong>Visitors</strong><small>Unavailable until an approved provider is configured.</small></div><div><strong>Conversions</strong><small>Unavailable until event tracking is explicitly enabled.</small></div><div><strong>Performance</strong><small>Use Site Health for calculated checks; production measurements remain unavailable.</small></div></div></section></div>`;
  };
  const renderWebsites = () => {
    view.innerHTML = `<div class="customer-heading"><div><p class="eyebrow">Workspace / Websites</p><h1>Your websites</h1><p>Draft, publish, and manage every WebKiln experience from one place.</p></div><div class="heading-actions"><button class="ghost-btn" data-create-blank-site>Create blank site</button><button class="primary-btn" data-create-site>New website</button></div></div><div class="website-toolbar"><label>Workspace<select data-workspace-select>${workspaces.map((workspace) => `<option value="${esc(workspace.id)}" ${workspace.id === selectedWorkspaceId ? 'selected' : ''}>${esc(workspace.name)}</option>`).join('')}</select></label><label class="website-search">Search websites<input type="search" data-website-search placeholder="Name, slug, or domain" /></label><label>Status<select data-website-filter><option value="all">All websites</option><option value="drafts">Drafts</option><option value="published">Published</option><option value="archived">Archived</option></select></label><label>Sort<select data-website-sort><option value="recent">Recently edited</option><option value="name">Name</option><option value="status">Status</option></select></label><span data-website-count>${sites.length} website${sites.length === 1 ? '' : 's'}</span></div><div class="website-grid" data-website-grid></div>`;
    bindCommon();
    const renderWebsiteGrid = () => {
      const query = document.querySelector<HTMLInputElement>('[data-website-search]')?.value ?? '';
      const filter = (document.querySelector<HTMLSelectElement>('[data-website-filter]')?.value ??
        'all') as WebsiteFilter;
      const sort = (document.querySelector<HTMLSelectElement>('[data-website-sort]')?.value ??
        'recent') as WebsiteSort;
      const filtered = filterAndSortWebsites(sites, query, filter, sort);
      const grid = document.querySelector<HTMLElement>('[data-website-grid]');
      const count = document.querySelector<HTMLElement>('[data-website-count]');
      if (grid)
        grid.innerHTML =
          filtered.map(siteCard).join('') ||
          (filter === 'archived'
            ? empty(
                'No archived websites',
                'Archived websites will appear here when you archive them.',
              )
            : sites.length
              ? empty('No matching websites', 'Try a different search or filter.')
              : empty('No websites yet', 'Create a blank website or start from a template.'));
      if (count)
        count.textContent = `${filtered.length} website${filtered.length === 1 ? '' : 's'}`;
      bindCommon();
    };
    renderWebsiteGrid();
    document.querySelector('[data-website-search]')?.addEventListener('input', renderWebsiteGrid);
    document.querySelector('[data-website-filter]')?.addEventListener('change', renderWebsiteGrid);
    document.querySelector('[data-website-sort]')?.addEventListener('change', renderWebsiteGrid);
    document
      .querySelector<HTMLSelectElement>('[data-workspace-select]')
      ?.addEventListener('change', (event) => {
        selectedWorkspaceId = (event.target as HTMLSelectElement).value;
        void loadSites();
      });
  };
  const renderTemplates = () => {
    view.innerHTML = `<div class="customer-heading"><div><p class="eyebrow">Templates</p><h1>Start with a point of view.</h1><p>Each template is a real website starting point, not a decorative demo.</p></div></div><div class="template-grid">${WEBKILN_TEMPLATES.map((template) => `<article class="template-card"><div class="template-preview"><span></span><i></i><b></b></div><p class="eyebrow">${esc(template.category)} · ${esc(template.websiteType)}</p><h2>${esc(template.name)}</h2><p>${esc(template.description)}</p><small>${template.pages.length} starter pages · ${esc(template.goal)} goal</small><button class="ghost-btn" type="button" data-template="${template.id}">Use template</button></article>`).join('')}</div>`;
    document.querySelectorAll<HTMLButtonElement>('[data-template]').forEach((button) =>
      button.addEventListener('click', () => {
        if (button.dataset.template === 'blank') {
          void handleCreateBlankSite();
        } else {
          navigate(
            `/onboarding?template=${encodeURIComponent(button.dataset.template ?? 'blank')}`,
          );
        }
      }),
    );
  };
  const renderCollections = async () => {
    if (view.dataset.section !== 'collections') return;
    const version = renderVersion;
    view.innerHTML = loading('Loading collections');
    try {
      const collections = await cloud.listCollections(selectedWorkspaceId);
      if (version !== renderVersion) return;
      view.innerHTML = `<div class="customer-heading"><div><p class="eyebrow">Workspace / Collections</p><h1>Reusable content</h1><p>Keep structured content separate from layout, then bind it into any page.</p></div><button class="primary-btn" data-create-collection>New collection</button></div><div class="website-grid">${collections.map((collection) => `<article class="dashboard-panel collection-card"><div class="collection-mark">{ }</div><h2>${esc(collection.name)}</h2><p class="website-url">${esc(collection.slug)}</p><div class="website-meta"><span>${collection.fields.length} fields</span><span>${collection.permissions.read} read access</span></div><div class="website-actions"><button class="ghost-btn" data-export-collection="${collection.id}">Export CSV</button><button class="primary-btn" data-records-collection="${collection.id}">View records</button></div></article>`).join('') || empty('No collections yet', 'Create a collection for posts, products, people, or any structured content.')}</div><div data-collection-records></div>`;
      document.querySelector('[data-create-collection]')?.addEventListener('click', async () => {
        const name = await askText('Collection name');
        if (!name?.trim()) return;
        try {
          await cloud.createCollection(selectedWorkspaceId, {
            name: name.trim(),
            fields: [
              { name: 'Title', slug: 'title', type: 'text', required: true },
              { name: 'Body', slug: 'body', type: 'rich-text' },
              { name: 'Slug', slug: 'slug', type: 'slug', required: true, unique: true },
            ],
          });
          say('Collection created.');
          await renderCollections();
        } catch (error) {
          say(error instanceof Error ? error.message : 'Could not create collection.');
        }
      });
      document.querySelectorAll<HTMLButtonElement>('[data-export-collection]').forEach((button) =>
        button.addEventListener('click', async () => {
          try {
            const csv = await cloud.exportCollection(button.dataset.exportCollection ?? '');
            const link = document.createElement('a');
            link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
            link.download = 'webkiln-collection.csv';
            link.click();
            URL.revokeObjectURL(link.href);
          } catch (error) {
            say(error instanceof Error ? error.message : 'Export failed.');
          }
        }),
      );
      document.querySelectorAll<HTMLButtonElement>('[data-records-collection]').forEach((button) =>
        button.addEventListener('click', async () => {
          const target = document.querySelector<HTMLElement>('[data-collection-records]');
          if (!target) return;
          try {
            const collectionId = button.dataset.recordsCollection ?? '';
            const collection = collections.find((item) => item.id === collectionId);
            if (!collection) return;
            let pageNumber = 1;
            const renderRecordPage = async (page = 1, search = '', status = '') => {
              const query = new URLSearchParams({ page: String(page), pageSize: '25' });
              if (search) query.set('search', search);
              if (status) query.set('status', status);
              const result = await cloud.listRecords(collectionId, query.toString());
              pageNumber = result.page;
              target.innerHTML = `<section class="dashboard-panel collection-records"><div class="panel-title"><div><h2>${esc(collection.name)} records</h2><span>${result.total} total · page ${result.page}</span></div><button class="primary-btn" type="button" data-new-record>New record</button></div><div class="submission-toolbar"><input type="search" data-record-search placeholder="Search records" aria-label="Search records" value="${esc(search)}" /><select data-record-status aria-label="Filter records"><option value="">All statuses</option><option value="draft" ${status === 'draft' ? 'selected' : ''}>Draft</option><option value="published" ${status === 'published' ? 'selected' : ''}>Published</option></select></div>${result.records.map((record) => `<div class="record-row" data-record-row="${esc(record.id)}"><strong>${esc(record.slug)}</strong><span class="status-chip ${record.status === 'published' ? 'published' : ''}">${record.status}</span><small>${new Date(record.updatedAt).toLocaleString()}</small><button class="ghost-btn" type="button" data-edit-record="${esc(record.id)}">Edit</button><button class="ghost-btn danger-action" type="button" data-delete-record="${esc(record.id)}">Delete</button></div>`).join('') || empty('No records', 'Create the first record for this collection.')}<div class="record-pagination"><button class="ghost-btn" type="button" data-record-prev ${result.page <= 1 ? 'disabled' : ''}>Previous</button><button class="ghost-btn" type="button" data-record-next ${result.page * result.pageSize >= result.total ? 'disabled' : ''}>Next</button></div></section>`;
              target
                .querySelector('[data-record-search]')
                ?.addEventListener(
                  'change',
                  () =>
                    void renderRecordPage(
                      1,
                      (
                        target.querySelector<HTMLInputElement>('[data-record-search]')?.value ?? ''
                      ).trim(),
                      target.querySelector<HTMLSelectElement>('[data-record-status]')?.value ?? '',
                    ),
                );
              target
                .querySelector('[data-record-status]')
                ?.addEventListener(
                  'change',
                  () =>
                    void renderRecordPage(
                      1,
                      target
                        .querySelector<HTMLInputElement>('[data-record-search]')
                        ?.value.trim() ?? '',
                      target.querySelector<HTMLSelectElement>('[data-record-status]')?.value ?? '',
                    ),
                );
              target
                .querySelector('[data-record-prev]')
                ?.addEventListener(
                  'click',
                  () => void renderRecordPage(pageNumber - 1, search, status),
                );
              target
                .querySelector('[data-record-next]')
                ?.addEventListener(
                  'click',
                  () => void renderRecordPage(pageNumber + 1, search, status),
                );
              target.querySelector('[data-new-record]')?.addEventListener('click', async () => {
                const value = await askText(
                  'Record JSON',
                  '{"title":"New record","slug":"new-record"}',
                );
                if (!value) return;
                try {
                  const data = JSON.parse(value) as Record<string, unknown>;
                  await cloud.createRecord(collectionId, data, 'draft');
                  say('Draft record created.');
                  await renderRecordPage(pageNumber, search, status);
                } catch (error) {
                  say(
                    error instanceof Error
                      ? error.message
                      : 'Record JSON is invalid or could not be saved.',
                  );
                }
              });
              target.querySelectorAll<HTMLButtonElement>('[data-edit-record]').forEach((action) =>
                action.addEventListener('click', async () => {
                  const record = result.records.find(
                    (item) => item.id === action.dataset.editRecord,
                  );
                  if (!record) return;
                  const value = await askText('Record JSON', JSON.stringify(record.data));
                  if (!value) return;
                  try {
                    await cloud.updateRecord(
                      record.id,
                      JSON.parse(value) as Record<string, unknown>,
                    );
                    say('Record updated.');
                    await renderRecordPage(pageNumber, search, status);
                  } catch (error) {
                    say(
                      error instanceof Error
                        ? error.message
                        : 'Record JSON is invalid or could not be updated.',
                    );
                  }
                }),
              );
              target.querySelectorAll<HTMLButtonElement>('[data-delete-record]').forEach((action) =>
                action.addEventListener('click', async () => {
                  if (!(await askConfirm('Delete this record?'))) return;
                  try {
                    await cloud.deleteRecord(action.dataset.deleteRecord ?? '');
                    say('Record deleted.');
                    await renderRecordPage(pageNumber, search, status);
                  } catch (error) {
                    say(error instanceof Error ? error.message : 'Could not delete record.');
                  }
                }),
              );
            };
            await renderRecordPage();
          } catch (error) {
            say(error instanceof Error ? error.message : 'Could not load records.');
          }
        }),
      );
    } catch (error) {
      if (version === renderVersion) view.innerHTML = errorState(error, 'collections');
    }
  };
  const renderAutomations = async () => {
    if (view.dataset.section !== 'automations') return;
    const version = renderVersion;
    view.innerHTML = loading('Loading automations');
    try {
      const automations = await cloud.listAutomations(selectedWorkspaceId);
      if (version !== renderVersion) return;
      view.innerHTML = `<div class="customer-heading"><div><p class="eyebrow">Workspace / Automations</p><h1>Visual automations</h1><p>Connect a trigger to conditions and actions without exposing credentials to the browser.</p></div><button class="primary-btn" data-create-automation>New automation</button></div><div class="automation-list">${automations.map((flow) => `<article class="dashboard-panel automation-card"><div class="automation-flow"><span class="flow-node">${esc(flow.triggerType)}</span><b>→</b><span class="flow-node">${flow.graph.conditions.length} conditions</span><b>→</b><span class="flow-node">${flow.graph.actions.length} actions</span></div><div class="automation-card-footer"><div><h2>${esc(flow.name)}</h2><small>${flow.status} · retry up to ${flow.retryPolicy.maxAttempts} times</small></div><div class="website-actions"><button class="ghost-btn" data-edit-automation="${flow.id}">Edit flow</button><button class="ghost-btn" data-toggle-automation="${flow.id}" data-status="${flow.status}">${flow.status === 'enabled' ? 'Disable' : 'Enable'}</button></div></div></article>`).join('') || empty('No automations yet', 'Create a draft flow with a trigger, conditions, and actions.')}</div><div data-automation-editor></div>`;
      document.querySelector('[data-create-automation]')?.addEventListener('click', async () => {
        const name = await askText('Automation name');
        if (!name?.trim()) return;
        try {
          await cloud.createAutomation(selectedWorkspaceId, {
            name: name.trim(),
            triggerType: 'form.submitted',
            graph: { conditions: [], actions: [{ type: 'log-event', config: {} }] },
          });
          say('Draft automation created.');
          await renderAutomations();
        } catch (error) {
          say(error instanceof Error ? error.message : 'Could not create automation.');
        }
      });
      document.querySelectorAll<HTMLButtonElement>('[data-toggle-automation]').forEach((button) =>
        button.addEventListener('click', async () => {
          try {
            const enabled = button.dataset.status !== 'enabled';
            await cloud.updateAutomation(button.dataset.toggleAutomation ?? '', {
              status: enabled ? 'enabled' : 'disabled',
            });
            say(enabled ? 'Automation enabled.' : 'Automation disabled.');
            await renderAutomations();
          } catch (error) {
            say(error instanceof Error ? error.message : 'Could not update automation.');
          }
        }),
      );
      const renderAutomationEditor = (flow: Automation) => {
        const target = document.querySelector<HTMLElement>('[data-automation-editor]');
        if (!target) return;
        let currentTrigger = flow.triggerType;
        let maxAttempts = flow.retryPolicy.maxAttempts;
        let backoffSeconds = flow.retryPolicy.backoffSeconds;
        let conditions = flow.graph.conditions.map((item) => ({ ...item }));
        let actions = flow.graph.actions.map((item) => ({ ...item, config: { ...item.config } }));
        const formatActionValue = (cfg: Record<string, unknown>): string => {
          const extraEntries = Object.entries(cfg).filter(
            ([k, v]) => k !== 'value' && v !== undefined && v !== '',
          );
          if (!extraEntries.length) return String(cfg.value ?? '');
          const extraObj = Object.fromEntries(extraEntries);
          if (typeof cfg.value === 'string' && cfg.value.trim()) {
            const trimmed = cfg.value.trim();
            if (trimmed.startsWith('{')) {
              try {
                const parsed = JSON.parse(trimmed) as Record<string, unknown>;
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                  const hasAllExtra = extraEntries.every(([k]) => k in parsed);
                  if (hasAllExtra) return trimmed;
                  return JSON.stringify({ ...parsed, ...extraObj });
                }
              } catch (err) {
                void err;
              }
            }
            return JSON.stringify({ ...extraObj, value: trimmed });
          }
          return JSON.stringify(extraObj);
        };
        const parseActionValue = (raw: string): Record<string, unknown> => {
          const trimmed = raw.trim();
          if (trimmed.startsWith('{')) {
            try {
              const parsed = JSON.parse(trimmed) as Record<string, unknown>;
              if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                return { ...parsed, value: trimmed };
              }
            } catch (err) {
              void err;
            }
          }
          return { value: trimmed };
        };
        const syncAutomationFromDom = () => {
          const trigEl = target.querySelector<HTMLSelectElement>('[data-auto-trigger]');
          if (trigEl) currentTrigger = trigEl.value;
          const retEl = target.querySelector<HTMLInputElement>('[data-auto-retries]');
          if (retEl) maxAttempts = Number(retEl.value ?? 0);
          const backEl = target.querySelector<HTMLInputElement>('[data-auto-backoff]');
          if (backEl) backoffSeconds = Number(backEl.value ?? 0);
          const condRows = target.querySelectorAll<HTMLElement>('[data-condition-index]');
          if (condRows.length || target.querySelector('.automation-builder')) {
            conditions = Array.from(condRows).map((row) => ({
              field:
                row.querySelector<HTMLInputElement>('[data-condition-field]')?.value.trim() ?? '',
              operator:
                row.querySelector<HTMLSelectElement>('[data-condition-operator]')?.value ??
                'equals',
              value:
                row.querySelector<HTMLInputElement>('[data-condition-value]')?.value.trim() ?? '',
            }));
          }
          const actionRows = target.querySelectorAll<HTMLElement>('[data-action-index]');
          if (actionRows.length || target.querySelector('.automation-builder')) {
            actions = Array.from(actionRows).map((row) => ({
              type:
                row.querySelector<HTMLSelectElement>('[data-action-type]')?.value ?? 'log-event',
              config: parseActionValue(
                row.querySelector<HTMLInputElement>('[data-action-value]')?.value ?? '',
              ),
            }));
          }
        };
        const draw = () => {
          target.innerHTML = `<section class="dashboard-panel automation-builder" aria-labelledby="automationBuilderTitle"><div class="panel-title"><div><p class="eyebrow">Automation builder</p><h2 id="automationBuilderTitle">${esc(flow.name)}</h2></div><span>Draft graph</span></div><div class="automation-builder-grid"><label>Trigger<select data-auto-trigger>${AUTOMATION_TRIGGERS.map((trigger) => `<option value="${trigger}" ${trigger === currentTrigger ? 'selected' : ''}>${trigger.replaceAll('.', ' ')}</option>`).join('')}</select></label><label>Max retries<input type="number" min="0" max="5" data-auto-retries value="${maxAttempts}" /></label><label>Backoff seconds<input type="number" min="0" max="3600" data-auto-backoff value="${backoffSeconds}" /></label></div><div class="automation-builder-section"><div class="panel-title"><h3>Conditions</h3><button class="ghost-btn" type="button" data-add-condition>Add condition</button></div><div class="automation-builder-list">${conditions.map((condition, index) => `<div class="automation-builder-row" data-condition-index="${index}"><input data-condition-field value="${esc(condition.field)}" placeholder="Field" aria-label="Condition field" /><select data-condition-operator><option value="equals" ${condition.operator === 'equals' ? 'selected' : ''}>equals</option><option value="contains" ${condition.operator === 'contains' ? 'selected' : ''}>contains</option><option value="exists" ${condition.operator === 'exists' ? 'selected' : ''}>exists</option></select><input data-condition-value value="${esc(condition.value ?? '')}" placeholder="Value" aria-label="Condition value" /><button class="ghost-btn danger-action" type="button" data-remove-condition="${index}">Remove</button></div>`).join('') || '<p class="panel-note">No conditions: every matching trigger runs.</p>'}</div></div><div class="automation-builder-section"><div class="panel-title"><h3>Actions</h3><button class="ghost-btn" type="button" data-add-action>Add action</button></div><div class="automation-builder-list">${actions.map((action, index) => `<div class="automation-builder-row" data-action-index="${index}"><select data-action-type>${AUTOMATION_ACTIONS.map((type) => `<option value="${type}" ${type === action.type ? 'selected' : ''}>${type.replaceAll('-', ' ')}</option>`).join('')}</select><input data-action-value value="${esc(formatActionValue(action.config))}" placeholder="Action value" aria-label="Action value" /><button class="ghost-btn danger-action" type="button" data-remove-action="${index}">Remove</button></div>`).join('') || '<p class="panel-note">Add at least one action to make the flow useful.</p>'}</div></div><p class="panel-note">Webhook credentials and email delivery remain provider-bound and are never shown in this editor.</p><div class="form-builder-actions"><button class="primary-btn" type="button" data-save-automation>Save automation</button></div></section>`;
          target.querySelector('[data-add-condition]')?.addEventListener('click', () => {
            syncAutomationFromDom();
            conditions.push({ field: '', operator: 'equals', value: '' });
            draw();
          });
          target.querySelector('[data-add-action]')?.addEventListener('click', () => {
            syncAutomationFromDom();
            actions.push({ type: 'log-event', config: {} });
            draw();
          });
          target.querySelectorAll<HTMLButtonElement>('[data-remove-condition]').forEach((button) =>
            button.addEventListener('click', () => {
              syncAutomationFromDom();
              conditions.splice(Number(button.dataset.removeCondition), 1);
              draw();
            }),
          );
          target.querySelectorAll<HTMLButtonElement>('[data-remove-action]').forEach((button) =>
            button.addEventListener('click', () => {
              syncAutomationFromDom();
              actions.splice(Number(button.dataset.removeAction), 1);
              draw();
            }),
          );
          target.querySelector('[data-save-automation]')?.addEventListener('click', async () => {
            syncAutomationFromDom();
            const graph = { conditions, actions };
            const validationError = validateAutomationGraph(graph);
            if (validationError) {
              say(validationError);
              return;
            }
            try {
              await cloud.updateAutomation(flow.id, {
                triggerType: currentTrigger,
                graph,
                retryPolicy: {
                  maxAttempts,
                  backoffSeconds,
                },
              });
              say('Automation saved.');
              await renderAutomations();
            } catch (error) {
              say(error instanceof Error ? error.message : 'Could not save automation.');
            }
          });
        };
        draw();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };
      document.querySelectorAll<HTMLButtonElement>('[data-edit-automation]').forEach((button) =>
        button.addEventListener('click', () => {
          const flow = automations.find((item) => item.id === button.dataset.editAutomation);
          if (flow) renderAutomationEditor(flow);
        }),
      );
    } catch (error) {
      if (version === renderVersion) view.innerHTML = errorState(error, 'automations');
    }
  };
  const renderForms = async () => {
    if (view.dataset.section !== 'forms') return;
    const version = renderVersion;
    view.innerHTML = loading('Loading forms');
    try {
      const forms = (
        await Promise.all(
          sites.map((site) =>
            cloud
              .listForms(site.id)
              .then((items) => items.map((form) => ({ ...form, siteName: site.name }))),
          ),
        )
      ).flat();
      if (version !== renderVersion) return;
      view.innerHTML = `<div class="customer-heading"><div><p class="eyebrow">Workspace / Forms</p><h1>Production-ready forms</h1><p>Build accessible, validated forms with server-side storage. File uploads are intentionally unavailable.</p></div><button class="primary-btn" data-create-form ${sites[0] ? '' : 'disabled'}>New form</button></div><div class="website-grid">${forms.map((form) => `<article class="dashboard-panel collection-card"><div class="collection-mark">⌁</div><h2>${esc(form.name)}</h2><p class="website-url">${esc(form.siteName)} · /forms/${esc(form.slug)}</p><div class="website-meta"><span>${form.fields.length} fields</span><span>${form.settings.honeypot === false ? 'Honeypot off' : 'Honeypot on'}</span></div><div class="website-actions"><button class="primary-btn" data-submissions-form="${form.id}">View submissions</button><button class="ghost-btn" data-edit-form="${form.id}">Edit fields</button></div></article>`).join('') || empty('No forms yet', 'Create a form with server-side validation and D1 submission storage.')}</div><div data-form-editor></div><div data-form-submissions></div>`;
      document.querySelector('[data-create-form]')?.addEventListener('click', async () => {
        const name = await askText('Form name');
        if (!name?.trim() || !sites[0]) return;
        try {
          await cloud.createForm(sites[0].id, name.trim(), [
            { name: 'name', type: 'text', label: 'Name', required: true },
            { name: 'email', type: 'email', label: 'Email', required: true },
            { name: 'message', type: 'textarea', label: 'Message', required: true },
            { name: 'consent', type: 'consent', label: 'I agree to be contacted', required: true },
          ]);
          say('Form created.');
          await renderForms();
        } catch (error) {
          say(error instanceof Error ? error.message : 'Could not create form.');
        }
      });
      document.querySelectorAll<HTMLButtonElement>('[data-edit-form]').forEach((button) =>
        button.addEventListener('click', async () => {
          const form = forms.find((item) => item.id === button.dataset.editForm);
          if (!form) return;
          renderFormEditor(form);
        }),
      );
      const renderFormEditor = (form: FormDefinition & { siteName?: string }) => {
        const target = document.querySelector<HTMLElement>('[data-form-editor]');
        if (!target) return;
        let fields = form.fields.map((field) => ({ ...field }));
        let honeypot = form.settings.honeypot !== false;
        const syncFormFieldsFromDom = () => {
          const hpInput = target.querySelector<HTMLInputElement>('[data-form-honeypot]');
          if (hpInput) honeypot = hpInput.checked;
          const cards = target.querySelectorAll<HTMLElement>('[data-form-field]');
          if (!cards.length && !target.querySelector('.form-builder')) return;
          fields = Array.from(cards).map((card) => {
            const name =
              card.querySelector<HTMLInputElement>('[data-field-name]')?.value.trim() ?? '';
            const label =
              card.querySelector<HTMLInputElement>('[data-field-label]')?.value.trim() ?? '';
            const type = (card.querySelector<HTMLSelectElement>('[data-field-type]')?.value ??
              'text') as FormFieldType;
            const options = card
              .querySelector<HTMLInputElement>('[data-field-options]')
              ?.value.split(',')
              .map((item) => item.trim())
              .filter(Boolean);
            const conditionField =
              card.querySelector<HTMLSelectElement>('[data-field-condition-field]')?.value ?? '';
            const conditionValue =
              card.querySelector<HTMLInputElement>('[data-field-condition-value]')?.value.trim() ??
              '';
            const step = Number(
              card.querySelector<HTMLInputElement>('[data-field-step]')?.value ?? 1,
            );
            const pattern =
              card.querySelector<HTMLInputElement>('[data-field-pattern]')?.value.trim() ?? '';
            return {
              id: card.dataset.formField ?? `field-${Date.now()}`,
              name,
              label,
              type,
              required:
                card.querySelector<HTMLInputElement>('[data-field-required]')?.checked ?? false,
              ...(options?.length ? { options } : {}),
              ...(pattern ? { validation: { pattern } } : {}),
              step: Number.isInteger(step) && step > 0 ? step : 1,
              ...(conditionField && conditionValue
                ? { conditional: { field: conditionField, equals: conditionValue } }
                : {}),
            };
          });
        };
        const draw = () => {
          target.innerHTML = `<section class="dashboard-panel form-builder" aria-labelledby="formBuilderTitle"><div class="panel-title"><div><p class="eyebrow">Form builder</p><h2 id="formBuilderTitle">${esc(form.name)}</h2></div><span>${fields.length} fields</span></div><p class="panel-note">Build the form visually. Server-side validation remains authoritative when submissions arrive.</p><div class="form-field-list">${
            fields
              .map(
                (field, index) =>
                  `<article class="form-field-card" data-form-field="${esc(field.id)}"><div class="form-field-head"><strong>Field ${index + 1}</strong><button class="ghost-btn danger-action" type="button" data-remove-form-field="${esc(field.id)}">Remove</button></div><div class="form-field-grid"><label>Type<select data-field-type>${FORM_FIELD_TYPES.map((type) => `<option value="${type}" ${type === field.type ? 'selected' : ''}>${type.replace('-', ' ')}</option>`).join('')}</select></label><label>Field name<input data-field-name value="${esc(field.name)}" required pattern="[A-Za-z][A-Za-z0-9_\\-]*" /></label><label class="form-field-wide">Visible label<input data-field-label value="${esc(field.label)}" required /></label><label class="form-field-wide">Options<input data-field-options value="${esc((field.options ?? []).join(', '))}" placeholder="One, Two, Three" /></label><label class="form-field-wide">Validation pattern<input data-field-pattern value="${esc(String(field.validation?.pattern ?? ''))}" placeholder="Optional regular expression" /></label><label>Step<input type="number" min="1" max="20" data-field-step value="${field.step ?? 1}" /></label><label>Show when<select data-field-condition-field><option value="">Always visible</option>${fields
                    .filter((candidate) => candidate.id !== field.id)
                    .map(
                      (candidate) =>
                        `<option value="${esc(candidate.name)}" ${field.conditional?.field === candidate.name ? 'selected' : ''}>${esc(candidate.label)}</option>`,
                    )
                    .join(
                      '',
                    )}</select></label><label>Equals<input data-field-condition-value value="${esc(field.conditional?.equals ?? '')}" placeholder="Matching value" /></label><label class="toggle-row form-field-wide"><span>Required</span><input type="checkbox" data-field-required ${field.required ? 'checked' : ''} /></label></div></article>`,
              )
              .join('') || '<p class="empty-state">Add a field to begin.</p>'
          }</div><div class="form-builder-actions"><button class="ghost-btn" type="button" data-add-form-field>Add field</button><label class="toggle-row"><span>Spam honeypot</span><input type="checkbox" data-form-honeypot ${honeypot ? 'checked' : ''} /></label><button class="primary-btn" type="button" data-save-form>Save form</button></div></section>`;
          target.querySelectorAll<HTMLButtonElement>('[data-remove-form-field]').forEach((remove) =>
            remove.addEventListener('click', () => {
              syncFormFieldsFromDom();
              fields = fields.filter((field) => field.id !== remove.dataset.removeFormField);
              draw();
            }),
          );
          target.querySelector('[data-add-form-field]')?.addEventListener('click', () => {
            syncFormFieldsFromDom();
            fields.push({
              id: `field-${Date.now()}-${fields.length + 1}`,
              name: `field${fields.length + 1}`,
              type: 'text',
              label: 'New field',
              required: false,
            });
            draw();
          });
          target.querySelector('[data-save-form]')?.addEventListener('click', async () => {
            syncFormFieldsFromDom();
            const nextFields: FormField[] = fields;
            const invalid = nextFields.some(
              (f) => !/^[A-Za-z][A-Za-z0-9_-]*$/.test(f.name) || !f.label,
            );
            const validationError = invalid
              ? 'Field labels and names are required.'
              : validateFormFields(nextFields);
            if (validationError) {
              say(validationError);
              return;
            }
            try {
              await cloud.updateForm(form.id, {
                fields: nextFields,
                settings: {
                  ...form.settings,
                  honeypot,
                },
              });
              say('Form saved.');
              await renderForms();
            } catch (error) {
              say(error instanceof Error ? error.message : 'Could not save the form.');
            }
          });
        };
        draw();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };
      const renderSubmissionPanel = (formId: string, submissions: FormSubmission[]) => {
        const target = document.querySelector<HTMLElement>('[data-form-submissions]');
        if (!target) return;
        const draw = () => {
          const query =
            document
              .querySelector<HTMLInputElement>('[data-submission-search]')
              ?.value.toLowerCase() ?? '';
          const status =
            document.querySelector<HTMLSelectElement>('[data-submission-status]')?.value ?? '';
          const filtered = submissions.filter((submission) => {
            const matchesStatus = !status || submission.status === status;
            const matchesQuery =
              !query || JSON.stringify(submission.data).toLowerCase().includes(query);
            return matchesStatus && matchesQuery;
          });
          target.querySelector<HTMLElement>('[data-submission-list]')!.innerHTML =
            filtered
              .map(
                (submission) =>
                  `<div class="record-row" data-submission-row="${esc(submission.id)}"><strong>${new Date(submission.createdAt).toLocaleString()}</strong><span class="status-chip ${submission.status === 'archived' ? '' : 'published'}">${esc(submission.status)}</span><button class="ghost-btn" type="button" data-submission-read="${esc(submission.id)}">${submission.status === 'read' ? 'Mark received' : 'Mark read'}</button><button class="ghost-btn danger-action" type="button" data-submission-delete="${esc(submission.id)}">Delete</button><dl class="submission-data">${Object.entries(
                    submission.data,
                  )
                    .map(
                      ([key, value]) =>
                        `<div><dt>${esc(key)}</dt><dd>${esc(Array.isArray(value) ? value.join(', ') : typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value ?? ''))}</dd></div>`,
                    )
                    .join('')}</dl></div>`,
              )
              .join('') || empty('No matching submissions', 'Try another search or status filter.');
          target.querySelectorAll<HTMLButtonElement>('[data-submission-read]').forEach((action) =>
            action.addEventListener('click', async () => {
              const submission = submissions.find(
                (item) => item.id === action.dataset.submissionRead,
              );
              if (!submission) return;
              const next = submission.status === 'read' ? 'received' : 'read';
              try {
                const updated = await cloud.updateSubmission(formId, submission.id, next);
                Object.assign(submission, updated);
                draw();
              } catch (error) {
                say(error instanceof Error ? error.message : 'Could not update submission.');
              }
            }),
          );
          target.querySelectorAll<HTMLButtonElement>('[data-submission-delete]').forEach((action) =>
            action.addEventListener('click', async () => {
              if (!(await askConfirm('Delete this submission?'))) return;
              try {
                await cloud.deleteSubmission(formId, action.dataset.submissionDelete ?? '');
                const index = submissions.findIndex(
                  (item) => item.id === action.dataset.submissionDelete,
                );
                if (index >= 0) submissions.splice(index, 1);
                draw();
              } catch (error) {
                say(error instanceof Error ? error.message : 'Could not delete submission.');
              }
            }),
          );
        };
        target.innerHTML = `<section class="dashboard-panel collection-records"><div class="panel-title"><h2>Submissions</h2><span>${submissions.length} stored · latest 200</span></div><div class="submission-toolbar"><input type="search" data-submission-search placeholder="Search submissions" aria-label="Search submissions" /><select data-submission-status aria-label="Filter submissions"><option value="">All statuses</option><option value="received">Received</option><option value="read">Read</option><option value="archived">Archived</option></select><button class="ghost-btn" type="button" data-submission-export>Export CSV</button></div><div data-submission-list></div></section>`;
        target.querySelector('[data-submission-search]')?.addEventListener('input', draw);
        target.querySelector('[data-submission-status]')?.addEventListener('change', draw);
        target.querySelector('[data-submission-export]')?.addEventListener('click', async () => {
          try {
            downloadFile(
              'webkiln-submissions.csv',
              await cloud.exportSubmissions(formId),
              'text/csv',
            );
          } catch (error) {
            say(error instanceof Error ? error.message : 'Could not export submissions.');
          }
        });
        draw();
      };
      document.querySelectorAll<HTMLButtonElement>('[data-submissions-form]').forEach((button) =>
        button.addEventListener('click', async () => {
          const target = document.querySelector<HTMLElement>('[data-form-submissions]');
          if (!target) return;
          try {
            const submissions = await cloud.listSubmissions(button.dataset.submissionsForm ?? '');
            renderSubmissionPanel(button.dataset.submissionsForm ?? '', submissions);
          } catch (error) {
            say(error instanceof Error ? error.message : 'Could not load submissions.');
          }
        }),
      );
    } catch (error) {
      if (version === renderVersion) view.innerHTML = errorState(error, 'forms');
    }
  };
  let currentAssets: AssetMetadata[] = [];
  let currentFolder = '';

  const folderSvg = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:-1px;margin-right:4px;"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/></svg>';
  const alertSvg = '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align:-1px;margin-right:3px;"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>';

  const getAssetTypeIcon = (mimeType: string) => {
    const mime = mimeType.toLowerCase();
    if (mime === 'image/svg+xml') {
      return '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="6" cy="6" r="3"/><circle cx="18" cy="18" r="3"/><path d="M6 9v3a6 6 0 0 0 6 6h3"/></svg>';
    }
    if (mime.startsWith('image/')) {
      return '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><rect width="18" height="18" x="3" y="3" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>';
    }
    if (mime.includes('pdf') || mime.startsWith('text/') || mime.includes('document')) {
      return '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>';
    }
    if (mime.startsWith('video/')) {
      return '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><rect width="18" height="18" x="3" y="3" rx="2"/><path d="m10 8 6 4-6 4V8z"/></svg>';
    }
    if (mime.startsWith('audio/')) {
      return '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>';
    }
    return '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>';
  };

  const showReplaceModal = (source: AssetMetadata, reload: () => void) => {
    const candidates = currentAssets.filter((a) => a.id !== source.id);
    if (!candidates.length) {
      say('No other assets available in this workspace to replace with.');
      return;
    }
    if (source.usageCount === 0) {
      say('This asset is currently unused (0 usages). There are no page references to replace.');
      return;
    }
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    backdrop.innerHTML = `<section class="setup-modal replace-asset-modal" role="dialog" aria-modal="true" aria-labelledby="replace-title"><div class="modal-head"><h2 id="replace-title">Replace asset everywhere</h2><button type="button" class="mini-btn" data-close aria-label="Close dialog">×</button></div><p class="modal-copy">Replace all occurrences of <strong>${esc(source.filename)}</strong> across all site pages.<br/><span class="replace-usage-warn">${alertSvg} Currently referenced ${source.usageCount} time(s). All page components pointing to this asset will be updated.</span></p><form data-replace-form><label class="modal-field-label">Replacement asset<select data-replacement-select required style="width: 100%; height: 38px; background: #0d1012; border: 1px solid var(--line); border-radius: 7px; color: #fff; padding: 0 10px; margin-top: 6px;">${candidates.map((c) => `<option value="${c.id}">${esc(c.filename)} (${esc(c.folder || '/')}, ${formatBytes(c.size)})</option>`).join('')}</select></label><div class="modal-actions"><button type="button" class="ghost-btn" data-close>Cancel</button><button type="submit" class="primary-btn">Confirm replacement</button></div></form></section>`;
    document.body.append(backdrop);
    const close = () => {
      release();
      backdrop.remove();
    };
    const release = bindDialog(backdrop, close);
    backdrop.querySelectorAll('[data-close]').forEach((btn) => btn.addEventListener('click', close));
    const form = backdrop.querySelector<HTMLFormElement>('[data-replace-form]');
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const select = backdrop.querySelector<HTMLSelectElement>('[data-replacement-select]');
      if (!select?.value) return;
      const submitBtn = form.querySelector<HTMLButtonElement>('button[type=submit]');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Replacing...';
      }
      try {
        const res = await cloud.replaceAsset(source.id, select.value);
        say(`Asset replaced in ${res.replacedPages} page(s).`);
        close();
        reload();
      } catch (err) {
        say(err instanceof Error ? err.message : 'Replacement failed.');
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Confirm replacement';
        }
      }
    });
  };

  const bindAssetActions = (reload: () => void) => {
    document.querySelectorAll<HTMLElement>('[data-focal-widget]').forEach((widget) => {
      const pad = widget.querySelector<HTMLElement>('[data-focal-pad]');
      const input = widget.querySelector<HTMLInputElement>('[data-focal]');
      const display = widget.querySelector<HTMLElement>('[data-focal-display]');
      const circle = widget.querySelector<HTMLElement>('.focal-point-circle');
      const crossH = widget.querySelector<HTMLElement>('.focal-crosshair-h');
      const crossV = widget.querySelector<HTMLElement>('.focal-crosshair-v');
      const centerBtn = widget.querySelector<HTMLButtonElement>('[data-focal-center]');

      const setCoords = (x: number, y: number) => {
        const cx = Math.max(0, Math.min(100, Math.round(x)));
        const cy = Math.max(0, Math.min(100, Math.round(y)));
        if (input) input.value = `${cx}, ${cy}`;
        if (display) display.textContent = `X: ${cx}%, Y: ${cy}%`;
        if (circle) {
          circle.style.left = `${cx}%`;
          circle.style.top = `${cy}%`;
        }
        if (crossH) crossH.style.top = `${cy}%`;
        if (crossV) crossV.style.left = `${cx}%`;
      };

      if (pad) {
        let isDragging = false;
        const handlePointer = (e: PointerEvent) => {
          const rect = pad.getBoundingClientRect();
          if (!rect.width || !rect.height) return;
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          setCoords(x, y);
        };
        pad.addEventListener('pointerdown', (e) => {
          isDragging = true;
          pad.setPointerCapture(e.pointerId);
          handlePointer(e);
        });
        pad.addEventListener('pointermove', (e) => {
          if (isDragging) handlePointer(e);
        });
        const stopDrag = (e: PointerEvent) => {
          if (isDragging) {
            isDragging = false;
            try { pad.releasePointerCapture(e.pointerId); } catch (_err) { void _err; }
          }
        };
        pad.addEventListener('pointerup', stopDrag);
        pad.addEventListener('pointercancel', stopDrag);
      }

      centerBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        setCoords(50, 50);
      });
    });

    document.querySelectorAll<HTMLButtonElement>('[data-asset-save]').forEach((button) =>
      button.addEventListener('click', async () => {
        const card = button.closest<HTMLElement>('[data-asset-card]');
        if (!card) return;
        try {
          await cloud.updateAsset(button.dataset.assetSave ?? '', {
            altText: card.querySelector<HTMLInputElement>('[data-alt]')?.value ?? '',
            caption: card.querySelector<HTMLInputElement>('[data-caption]')?.value ?? '',
            focalPoint: (() => {
              const [x, y] = (
                card.querySelector<HTMLInputElement>('[data-focal]')?.value ?? '50, 50'
              )
                .split(',')
                .map(Number);
              return {
                x: Number.isFinite(x) ? Math.max(0, Math.min(100, x)) : 50,
                y: Number.isFinite(y) ? Math.max(0, Math.min(100, y)) : 50,
              };
            })(),
            folder: card.querySelector<HTMLInputElement>('[data-folder]')?.value ?? '/',
            tags: (card.querySelector<HTMLInputElement>('[data-tags]')?.value ?? '')
              .split(',')
              .map((tag) => tag.trim())
              .filter(Boolean),
            brandGroup: card.querySelector<HTMLInputElement>('[data-brand]')?.value ?? '',
          });
          say('Asset details saved.');
        } catch (error) {
          say(error instanceof Error ? error.message : 'Could not save asset details.');
        }
      }),
    );

    document.querySelectorAll<HTMLButtonElement>('[data-asset-replace]').forEach((button) =>
      button.addEventListener('click', () => {
        const assetId = button.dataset.assetReplace;
        const target = currentAssets.find((a) => a.id === assetId);
        if (target) showReplaceModal(target, reload);
      }),
    );

    document.querySelectorAll<HTMLButtonElement>('[data-asset-delete]').forEach((button) =>
      button.addEventListener('click', async () => {
        if (!(await askConfirm('Delete this unused asset metadata?'))) return;
        try {
          await cloud.deleteAsset(button.dataset.assetDelete ?? '');
          say('Asset metadata deleted.');
          reload();
        } catch (error) {
          say(
            error instanceof Error
              ? error.message
              : 'Asset is still in use or could not be deleted.',
          );
        }
      }),
    );
  };

  const assetCard = (asset: AssetMetadata) => {
    const isDuplicate = duplicateIds.has(asset.id);
    const icon = getAssetTypeIcon(asset.mimeType);

    return `<article class="dashboard-panel asset-manager-card" data-asset-card>
      <div class="asset-manager-thumb">
        <span>${icon}</span>
        <small>${esc(asset.mimeType)}</small>
        <div style="position: absolute; top: 0.55rem; right: 0.7rem; display: flex; gap: 0.35rem;">
          ${isDuplicate ? `<span class="asset-badge badge-duplicate" title="Identical content hash found in workspace">${alertSvg}Duplicate</span>` : ''}
          <span class="asset-badge">${esc(asset.storageStatus)}</span>
        </div>
      </div>
      <div class="asset-manager-body">
        <h2>${esc(asset.filename)}</h2>
        <div class="asset-badge-group">
          <span class="asset-badge ${asset.usageCount ? 'badge-in-use' : 'badge-unused'}"><span class="badge-dot ${asset.usageCount ? 'dot-active' : ''}"></span>${asset.usageCount ? `${asset.usageCount} ${asset.usageCount === 1 ? 'usage' : 'usages'}` : 'Unused'}</span>
          <span class="asset-badge badge-folder">${folderSvg}${esc(asset.folder || '/')}</span>
          ${asset.brandGroup ? `<span class="brand-chip">${esc(asset.brandGroup)}</span>` : ''}
        </div>
        <small>${formatBytes(asset.size)}${asset.width && asset.height ? ` · ${asset.width}×${asset.height}px` : ''}</small>
        <label>Alt text<input data-alt value="${esc(asset.altText)}" placeholder="Describe the asset" /></label>
        <label>Caption<input data-caption value="${esc(asset.caption)}" placeholder="Optional caption" /></label>
        <label>Folder<input data-folder value="${esc(asset.folder || '/')}" placeholder="/heroes" /></label>
        <label>Tags<input data-tags value="${esc(asset.tags.join(', '))}" placeholder="brand, hero" /></label>
        <label>Brand group<input data-brand value="${esc(asset.brandGroup ?? '')}" placeholder="Optional" /></label>
        <div class="focal-field-group">
          <span class="focal-field-label">Focal point (object-position)</span>
          <div class="focal-point-widget" data-focal-widget="${asset.id}" title="Drag or click to position focal center">
            <div class="focal-point-pad" data-focal-pad>
              <div class="focal-crosshair-h" style="top: ${asset.focalPoint.y}%;"></div>
              <div class="focal-crosshair-v" style="left: ${asset.focalPoint.x}%;"></div>
              <div class="focal-point-circle" style="left: ${asset.focalPoint.x}%; top: ${asset.focalPoint.y}%;"></div>
            </div>
            <div class="focal-point-meta">
              <span data-focal-display>X: ${asset.focalPoint.x}%, Y: ${asset.focalPoint.y}%</span>
              <button type="button" class="focal-reset-btn" data-focal-center title="Reset to center (50%, 50%)">Center (50, 50)</button>
            </div>
            <input type="hidden" data-focal value="${asset.focalPoint.x}, ${asset.focalPoint.y}" />
          </div>
        </div>
        <div class="website-actions">
          <button class="primary-btn" data-asset-save="${asset.id}">Save details</button>
          <button class="ghost-btn" data-asset-replace="${asset.id}" ${asset.usageCount === 0 ? 'disabled title="Asset is unused: replace is only available when an asset is referenced on site pages"' : 'title="Replace references across all pages"'}>Replace everywhere</button>
          <button class="ghost-btn danger-action" data-asset-delete="${asset.id}" ${asset.usageCount ? 'disabled title="Asset is in use: replace references before deleting"' : ''}>${asset.usageCount ? 'In use' : 'Delete'}</button>
        </div>
      </div>
    </article>`;
  };

  const renderAssets = async () => {
    if (view.dataset.section !== 'assets') return;
    const version = renderVersion;
    view.innerHTML = loading('Loading asset library');
    if (!selectedWorkspaceId) {
      view.innerHTML = empty('No workspace selected', 'Create a workspace before managing assets.');
      return;
    }
    try {
      const assets = await cloud.listAssets(selectedWorkspaceId);
      if (version !== renderVersion) return;
      currentAssets = assets;
      duplicateIds = findDuplicateAssetIds(assets);
      const usage = assets.reduce((sum, asset) => sum + asset.size, 0);
      const folders = extractFolderHierarchy(assets);

      view.innerHTML = `<div class="customer-heading"><div><p class="eyebrow">Workspace / Assets</p><h1>Asset library</h1><p>Organize metadata, usage, and brand context. Binary storage is not configured in this free-tier milestone.</p></div><button class="primary-btn" data-register-asset ${sites[0] ? '' : 'disabled'}>Register metadata</button></div><section class="asset-capability dashboard-panel"><strong>Storage not configured</strong><span>Metadata is available now. Uploads, R2, WebP, AVIF, thumbnails, and responsive transformations remain disabled until an approved storage milestone.</span><b>${formatBytes(usage)} estimated metadata-linked usage · ${assets.length} assets</b></section><div class="asset-folders-bar" data-asset-folders><button type="button" class="folder-pill ${currentFolder === '' ? 'active' : ''}" data-folder-pill="">${folderSvg}All folders (${assets.length})</button>${folders.map((f) => `<button type="button" class="folder-pill ${currentFolder === f ? 'active' : ''}" data-folder-pill="${esc(f)}">${folderSvg}${esc(f)} (${assets.filter((a) => a.folder === f).length})</button>`).join('')}<button type="button" class="folder-pill new-folder-btn" data-new-folder>+ New folder</button></div><div class="asset-toolbar"><input type="search" data-asset-search placeholder="Search filename, tags, or alt text" aria-label="Search assets" /><select data-asset-type aria-label="Filter file type"><option value="">All file types</option><option value="image">Images</option><option value="vector">Vectors (SVG)</option><option value="document">Documents</option><option value="video">Video</option><option value="audio">Audio</option><option value="other">Other</option></select><select data-asset-sort aria-label="Sort assets"><option value="updated">Recently updated</option><option value="name">Name (A-Z)</option><option value="size">Largest first</option><option value="usage">Most used</option></select></div><div class="asset-manager-grid" data-asset-grid>${assets.map(assetCard).join('') || empty('No asset metadata yet', 'Register an asset reference or keep using the local editor asset system.')}</div>`;

      const reload = () => void renderAssets();

      const openRegisterModal = () => {
        if (!sites[0]) return;
        const backdrop = document.createElement('div');
        backdrop.className = 'modal-backdrop';
        backdrop.innerHTML = `<section class="setup-modal register-asset-modal" role="dialog" aria-modal="true" aria-labelledby="reg-title"><div class="modal-head"><h2 id="reg-title">Register asset metadata</h2><button type="button" class="mini-btn" data-close aria-label="Close dialog">×</button></div><div class="callout-box" style="background:rgba(214,173,97,0.08);border:1px solid rgba(214,173,97,0.25);border-radius:6px;padding:10px 12px;margin:12px 0 16px;font-size:12px;line-height:1.5;color:#d5d9d7;"><strong style="color:var(--gold,#d6ad61);display:block;margin-bottom:3px;">Metadata-only mode (No binary storage)</strong>Select a local file to inspect its properties (name, type, size, dimensions). Your file is processed locally by your browser: <strong>no binary data is uploaded to cloud storage</strong> in this milestone.</div><form data-reg-form><div style="margin-bottom:14px;"><input type="file" data-file-input style="display:none;" /><button type="button" class="ghost-btn" data-browse-btn style="width:100%;height:44px;border:1px dashed var(--line);display:flex;align-items:center;justify-content:center;gap:8px;"><span>Choose local file...</span></button><div data-file-preview style="margin-top:8px;font-size:12px;color:#9da6aa;">No file selected</div></div><label class="modal-field-label" style="display:block;margin-bottom:12px;">Asset filename<input type="text" data-name-input placeholder="e.g. hero-banner.png" style="width:100%;height:38px;background:#0d1012;border:1px solid var(--line);border-radius:7px;color:#fff;padding:0 10px;margin-top:6px;" /></label><label class="modal-field-label" style="display:block;margin-bottom:12px;">Target folder<input type="text" data-folder-input value="${esc(currentFolder || '/')}" placeholder="/heroes" style="width:100%;height:38px;background:#0d1012;border:1px solid var(--line);border-radius:7px;color:#fff;padding:0 10px;margin-top:6px;" /></label><div class="modal-actions"><button type="button" class="ghost-btn" data-close>Cancel</button><button type="submit" class="primary-btn" data-submit-btn disabled>Register metadata</button></div></form></section>`;
        document.body.append(backdrop);
        const close = () => {
          release();
          backdrop.remove();
        };
        const release = bindDialog(backdrop, close);
        backdrop.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', close));

        const fileInput = backdrop.querySelector<HTMLInputElement>('[data-file-input]')!;
        const browseBtn = backdrop.querySelector<HTMLButtonElement>('[data-browse-btn]')!;
        const preview = backdrop.querySelector<HTMLElement>('[data-file-preview]')!;
        const nameInput = backdrop.querySelector<HTMLInputElement>('[data-name-input]')!;
        const folderInput = backdrop.querySelector<HTMLInputElement>('[data-folder-input]')!;
        const submitBtn = backdrop.querySelector<HTMLButtonElement>('[data-submit-btn]')!;
        const form = backdrop.querySelector<HTMLFormElement>('[data-reg-form]')!;

        let selectedFile: File | null = null;
        let dims: { width?: number; height?: number } = {};

        browseBtn.addEventListener('click', () => fileInput.click());

        fileInput.addEventListener('change', async () => {
          const file = fileInput.files?.[0];
          if (!file) return;
          selectedFile = file;
          dims = {};
          if (file.type.startsWith('image/')) {
            dims = await new Promise((res) => {
              const u = URL.createObjectURL(file);
              const img = new Image();
              img.onload = () => {
                URL.revokeObjectURL(u);
                res({ width: img.naturalWidth, height: img.naturalHeight });
              };
              img.onerror = () => {
                URL.revokeObjectURL(u);
                res({});
              };
              img.src = u;
            });
          }
          const dimText = dims.width && dims.height ? ` · ${dims.width}×${dims.height}px` : '';
          preview.innerHTML = `<span style="color:var(--gold,#d6ad61);">✓</span> <strong>${esc(file.name)}</strong> (${formatBytes(file.size)}${dimText})`;
          if (nameInput && !nameInput.value.trim()) {
            nameInput.value = file.name;
          }
          submitBtn.disabled = false;
        });

        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          if (!selectedFile) return;
          submitBtn.disabled = true;
          submitBtn.textContent = 'Registering...';
          try {
            const finalName = nameInput.value.trim() || selectedFile.name;
            const mime = selectedFile.type || 'application/octet-stream';
            const hash = computeAssetContentHash(finalName, selectedFile.size, mime);
            await cloud.createAsset(selectedWorkspaceId, {
              siteId: sites[0].id,
              filename: finalName,
              mimeType: mime,
              size: selectedFile.size,
              folder: normalizeFolderPath(folderInput.value.trim() || currentFolder || '/'),
              width: dims.width,
              height: dims.height,
              contentHash: hash,
            });
            say(`Metadata registered for "${finalName}" (metadata only, no binary stored).`);
            close();
            reload();
          } catch (err) {
            say(err instanceof Error ? err.message : 'Could not register asset.');
            submitBtn.disabled = false;
            submitBtn.textContent = 'Register metadata';
          }
        });
      };

      document.querySelectorAll('[data-register-asset], [data-empty-trigger="data-register-asset"]').forEach((btn) => {
        btn.addEventListener('click', openRegisterModal);
      });

      document.querySelector('[data-new-folder]')?.addEventListener('click', async () => {
        const folderName = await askText('Folder path (e.g. /marketing or /heroes)');
        if (!folderName?.trim()) return;
        currentFolder = normalizeFolderPath(folderName.trim());
        void applyFilters();
      });

      const applyFilters = async () => {
        const query = new URLSearchParams();
        const search = document
          .querySelector<HTMLInputElement>('[data-asset-search]')
          ?.value.trim();
        const type = document.querySelector<HTMLSelectElement>('[data-asset-type]')?.value;
        const sort = document.querySelector<HTMLSelectElement>('[data-asset-sort]')?.value;
        if (currentFolder) query.set('folder', currentFolder);
        if (search) query.set('q', search);
        if (type) query.set('type', type);
        if (sort) query.set('sort', sort);

        const filtered = await cloud.listAssets(selectedWorkspaceId, query.toString());
        currentAssets = filtered;
        duplicateIds = findDuplicateAssetIds(filtered);
        const grid = document.querySelector<HTMLElement>('[data-asset-grid]');
        if (grid)
          grid.innerHTML =
            filtered.map(assetCard).join('') ||
            empty('No matching assets', 'Try a different search or filter.');

        document.querySelectorAll<HTMLButtonElement>('[data-folder-pill]').forEach((btn) => {
          btn.classList.toggle('active', (btn.dataset.folderPill ?? '') === currentFolder);
        });

        bindAssetActions(reload);
      };

      document.querySelectorAll<HTMLButtonElement>('[data-folder-pill]').forEach((btn) => {
        btn.addEventListener('click', () => {
          currentFolder = btn.dataset.folderPill ?? '';
          void applyFilters();
        });
      });

      document
        .querySelector('[data-asset-search]')
        ?.addEventListener('input', () => void applyFilters());
      document
        .querySelector('[data-asset-type]')
        ?.addEventListener('change', () => void applyFilters());
      document
        .querySelector('[data-asset-sort]')
        ?.addEventListener('change', () => void applyFilters());

      bindAssetActions(reload);
    } catch (error) {
      if (version === renderVersion) view.innerHTML = errorState(error, 'assets');
    }
  };


  const copyTextWithFallback = async (text: string): Promise<boolean> => {
    if (!text) return false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (_e) {
      void _e;
    }
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      textarea.style.pointerEvents = 'none';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);
      return successful;
    } catch (_e) {
      void _e;
      return false;
    }
  };

  const formatAuditAction = (action: string): string => {
    const map: Record<string, string> = {
      'workspace.ownership_transferred': 'Ownership Transferred',
      'workspace.member_invited': 'Member Invited',
      'workspace.member_updated': 'Role Updated',
      'workspace.member_removed': 'Member Removed',
      'member.role_changed': 'Role Updated',
      'member.removed': 'Member Removed',
      'invite.created': 'Invitation Created',
      'invite.accepted': 'Invitation Accepted',
      'site.created': 'Site Created',
      'site.published': 'Site Published',
      'site.deleted': 'Site Deleted',
      'security.login': 'User Sign In',
      'approval.requested': 'Approval Requested',
      'approval.decided': 'Approval Decided',
    };
    if (map[action]) return map[action];
    return action
      .split(/[._]+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };

  const getAuditActionClass = (action: string): string => {
    if (action.includes('transfer') || action.includes('ownership')) return 'pill-gold';
    if (action.includes('invite') || action.includes('create')) return 'pill-blue';
    if (action.includes('publish') || action.includes('accept') || action.includes('approved')) return 'pill-green';
    if (action.includes('remove') || action.includes('delete') || action.includes('reject')) return 'pill-red';
    if (action.includes('role') || action.includes('update')) return 'pill-purple';
    return 'pill-neutral';
  };

  const formatAuditDetails = (evt: AuditEvent): string => {
    const meta = evt.metadata || {};
    if (evt.action === 'workspace.ownership_transferred') {
      const next = meta.newOwnerUserId ? String(meta.newOwnerUserId).slice(0, 8) : 'new owner';
      return `Ownership transferred to user ${next}`;
    }
    if (evt.action === 'member.role_changed' || evt.action === 'workspace.member_updated') {
      return meta.role ? `Role changed to ${String(meta.role).replace('_', ' ')}` : 'Member role updated';
    }
    if (evt.action === 'invite.created' || evt.action === 'workspace.member_invited') {
      return meta.email ? `Invited ${String(meta.email)} as ${String(meta.role || 'reviewer')}` : 'Invitation sent';
    }
    if (evt.action === 'member.removed' || evt.action === 'workspace.member_removed') {
      return meta.memberUserId ? `Removed user ${String(meta.memberUserId).slice(0, 8)}` : 'Member removed from workspace';
    }
    if (evt.action === 'site.published') {
      return meta.siteName ? `Published site "${String(meta.siteName)}"` : `Published site ${evt.resourceId.slice(0, 8)}`;
    }
    if (evt.action === 'site.created') {
      return meta.siteName ? `Created site "${String(meta.siteName)}"` : `Created site ${evt.resourceId.slice(0, 8)}`;
    }
    if (meta.summary) return String(meta.summary);
    return `Resource: ${evt.resourceType} (${evt.resourceId.slice(0, 8)})`;
  };

  const renderTeam = async () => {
    if (view.dataset.section !== 'team') return;
    const version = renderVersion;
    if (!selectedWorkspaceId) return;
    view.innerHTML = loading('Loading workspace members');
    try {
      const [result, auditEvents] = await Promise.all([
        cloud.listMembers(selectedWorkspaceId),
        cloud.listAuditLog(selectedWorkspaceId).catch(() => [] as AuditEvent[]),
      ]);
      if (version !== renderVersion) return;

      const selectedWorkspace = workspaces.find((w) => w.id === selectedWorkspaceId);
      const isCurrentUserOwner =
        result.members.some((m) => m.userId === session.user.id && m.role === 'owner') ||
        selectedWorkspace?.ownerUserId === session.user.id;

      view.innerHTML = `
        <div class="customer-heading">
          <div>
            <p class="eyebrow">Workspace / Team</p>
            <h1>People and permissions</h1>
            <p>Roles are enforced by the Worker. Invitation email delivery is not connected; copy the secure invitation link instead.</p>
          </div>
          <button class="primary-btn" data-invite-member>Invite member</button>
        </div>

        <section class="dashboard-panel team-panel">
          <div class="panel-title">
            <h2>Members</h2>
            <span>${result.members.length} active</span>
          </div>
          ${result.members
            .map((member) => {
              const memberName = member.user?.name || member.user?.email || member.userId;
              const memberEmail = member.user?.email || member.userId;
              const initials = (memberName || 'U')
                .split(/[\s@._]+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((s) => s[0].toUpperCase())
                .join('') || 'U';
              const isOwner = member.role === 'owner';
              const isSelf = member.userId === session.user.id;

              return `
                <div class="team-row member-row" data-member-id="${esc(member.id)}">
                  <div class="team-member-info">
                    <div class="team-avatar" aria-hidden="true">${esc(initials)}</div>
                    <div class="team-meta">
                      <div class="team-name-line">
                        <strong class="team-name">${esc(memberName)}</strong>
                        ${isSelf ? '<span class="self-badge">You</span>' : ''}
                        ${isOwner ? '<span class="owner-badge">Owner</span>' : ''}
                      </div>
                      <small class="team-email">${esc(memberEmail)} · <span class="status-tag status-${esc(member.invitationStatus)}">${esc(member.invitationStatus)}</span></small>
                    </div>
                  </div>
                  <div class="team-actions">
                    ${isOwner
                      ? '<span class="team-role-display">Owner</span>'
                      : `<select data-member-role="${esc(member.id)}" aria-label="Change role for ${esc(memberName)}">
                          ${['administrator', 'designer', 'content_editor', 'reviewer', 'viewer']
                            .map(
                              (role) =>
                                `<option value="${role}" ${role === member.role ? 'selected' : ''}>${role.replace('_', ' ')}</option>`,
                            )
                            .join('')}
                        </select>`}
                    ${isCurrentUserOwner && !isOwner
                      ? `<button class="ghost-btn transfer-btn" data-transfer-ownership="${esc(member.id)}" data-member-name="${esc(memberName)}" type="button">Transfer ownership</button>`
                      : ''}
                    <button class="ghost-btn danger-action" data-remove-member="${esc(member.id)}" ${isOwner ? 'disabled title="Cannot remove workspace owner"' : ''} type="button">Remove</button>
                  </div>
                </div>
              `;
            })
            .join('')}
        </section>

        <section class="dashboard-panel team-panel">
          <div class="panel-title">
            <h2>Pending invitations</h2>
            <span>${result.invitations.length} pending</span>
          </div>
          ${result.invitations
            .map(
              (invite) => `
                <div class="team-row member-row invite-row" data-invite-id="${esc(invite.id)}">
                  <div class="team-member-info">
                    <div class="team-avatar invite-avatar" aria-hidden="true">✉</div>
                    <div class="team-meta">
                      <div class="team-name-line">
                        <strong class="team-name">${esc(invite.email)}</strong>
                        <span class="invite-role-tag">${esc(invite.role.replace('_', ' '))}</span>
                      </div>
                      <small class="team-email">Expires ${new Date(invite.expiresAt).toLocaleDateString()}</small>
                    </div>
                  </div>
                  <div class="team-actions invite-actions">
                    <button class="ghost-btn copy-invite-btn" data-copy-invite="${esc(invite.id)}" data-invite-url="${esc(invite.inviteUrl ?? '')}" type="button">Copy link</button>
                    <div class="invite-menu-wrap">
                      <button class="ghost-btn mini-icon-btn invite-menu-btn" data-invite-menu="${esc(invite.id)}" type="button" aria-label="More actions" title="More actions">···</button>
                      <div class="invite-dropdown-menu" id="inviteMenu_${esc(invite.id)}" hidden>
                        <button type="button" class="invite-menu-action" data-reveal-invite="${esc(invite.id)}" data-invite-email="${esc(invite.email)}" data-invite-role="${esc(invite.role)}" data-invite-url="${esc(invite.inviteUrl ?? '')}">Reveal link</button>
                        <button type="button" class="invite-menu-action" data-edit-invite="${esc(invite.id)}" data-invite-email="${esc(invite.email)}" data-current-role="${esc(invite.role)}">Edit role</button>
                        <button type="button" class="invite-menu-action danger-action" data-cancel-invite="${esc(invite.id)}" data-invite-email="${esc(invite.email)}">Cancel</button>
                      </div>
                    </div>
                  </div>
                </div>
              `,
            )
            .join('') || empty('No pending invitations', 'Invite a collaborator when you are ready.')}
        </section>

        <section class="dashboard-panel audit-panel">
          <div class="panel-title">
            <h2>Activity &amp; Security Audit Log</h2>
            <span>${auditEvents.length} event${auditEvents.length === 1 ? '' : 's'} recorded</span>
          </div>
          ${auditEvents.length === 0
            ? empty('No audit events yet', 'Workspace actions, role updates, and security events will be logged here.')
            : `<div class="audit-stream">
                ${auditEvents
                  .map((evt) => {
                    const actorName = evt.user?.name || evt.user?.email || (evt.userId ? evt.userId.slice(0, 8) : 'System');
                    const initials = (actorName || 'S')
                      .split(/[\s@._]+/)
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((s) => s[0].toUpperCase())
                      .join('') || 'S';
                    const formattedDate = new Date(evt.createdAt).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    });
                    const actionClass = getAuditActionClass(evt.action);
                    const actionLabel = formatAuditAction(evt.action);
                    const details = formatAuditDetails(evt);

                    return `
                      <div class="audit-row" data-audit-id="${esc(evt.id)}">
                        <div class="audit-actor-meta">
                          <div class="audit-avatar" aria-hidden="true">${esc(initials)}</div>
                          <div class="audit-actor-info">
                            <strong class="audit-actor-name">${esc(actorName)}</strong>
                            <time class="audit-time">${esc(formattedDate)}</time>
                          </div>
                        </div>
                        <div class="audit-event-content">
                          <span class="audit-action-pill ${actionClass}">${esc(actionLabel)}</span>
                          <span class="audit-details">${esc(details)}</span>
                        </div>
                        <div class="audit-resource-tag">
                          <small>${esc(evt.resourceType)}</small>
                        </div>
                      </div>
                    `;
                  })
                  .join('')}
              </div>`}
        </section>
      `;

      const openRevealLinkModal = (url: string, email: string, role: string) => {
        const backdrop = document.createElement('div');
        backdrop.className = 'modal-backdrop';
        backdrop.innerHTML = `
          <section class="setup-modal reveal-invite-modal" role="dialog" aria-modal="true" aria-labelledby="reveal-title">
            <div class="modal-head">
              <h2 id="reveal-title">Invitation link ready</h2>
              <button type="button" class="mini-btn" data-close aria-label="Close dialog">×</button>
            </div>
            <p class="modal-copy">Share this single-use link with <strong>${esc(email)}</strong> to let them join with the <strong>${esc(role.replace('_', ' '))}</strong> role.</p>
            <div class="copy-input-group" style="display: flex; gap: 8px; margin: 16px 0;">
              <input type="text" data-reveal-url readonly value="${esc(url)}" style="flex: 1; height: 38px; background: #0d1012; border: 1px solid var(--line); border-radius: 7px; color: #fff; padding: 0 10px; font-family: monospace; font-size: 11px;" />
              <button type="button" class="primary-btn" data-modal-copy>Copy</button>
            </div>
            <small style="color: var(--muted); font-size: 11px; display: block; margin-bottom: 16px;">Expires in 7 days · Email delivery is not connected</small>
            <div class="modal-actions">
              <button type="button" class="primary-btn" data-close>Done</button>
            </div>
          </section>
        `;
        document.body.append(backdrop);
        const close = () => {
          release();
          backdrop.remove();
        };
        const release = bindDialog(backdrop, close);
        backdrop.querySelectorAll('[data-close]').forEach((btn) => btn.addEventListener('click', close));

        const input = backdrop.querySelector<HTMLInputElement>('[data-reveal-url]');
        input?.select();

        const copyBtn = backdrop.querySelector<HTMLButtonElement>('[data-modal-copy]');
        copyBtn?.addEventListener('click', async () => {
          if (!input?.value) return;
          const copied = await copyTextWithFallback(input.value);
          if (copied) {
            copyBtn.textContent = 'Copied!';
            say('Invitation link copied to clipboard.');
            setTimeout(() => {
              if (copyBtn.isConnected) copyBtn.textContent = 'Copy';
            }, 2000);
          } else {
            input.select();
            say('Please copy the highlighted link manually (Ctrl+C).');
          }
        });

        if (url) {
          void copyTextWithFallback(url).then((ok) => {
            if (ok && copyBtn) {
              copyBtn.textContent = 'Copied!';
              setTimeout(() => {
                if (copyBtn.isConnected) copyBtn.textContent = 'Copy';
              }, 2000);
            }
          });
        }
      };

      const openInviteModal = () => {
        const backdrop = document.createElement('div');
        backdrop.className = 'modal-backdrop';
        backdrop.innerHTML = `
          <section class="setup-modal invite-member-modal" role="dialog" aria-modal="true" aria-labelledby="invite-title">
            <div class="modal-head">
              <h2 id="invite-title">Invite team member</h2>
              <button type="button" class="mini-btn" data-close aria-label="Close dialog">×</button>
            </div>
            <p class="modal-copy">Add a collaborator to this workspace. A single-use secure link will be created for them.</p>
            <form data-invite-form>
              <label class="modal-field-label">
                Email address
                <input type="email" data-invite-email required placeholder="collaborator@company.com" style="width: 100%; height: 38px; background: #0d1012; border: 1px solid var(--line); border-radius: 7px; color: #fff; padding: 0 10px; margin-top: 6px; box-sizing: border-box;" />
                <small data-invite-error style="color: #f56565; font-size: 11px; margin-top: 4px; display: none;"></small>
              </label>
              <label class="modal-field-label" style="margin-top: 14px; display: block;">
                Workspace Role
                <select data-invite-role required style="width: 100%; height: 38px; background: #0d1012; border: 1px solid var(--line); border-radius: 7px; color: #fff; padding: 0 10px; margin-top: 6px; box-sizing: border-box;">
                  <option value="reviewer" selected>Reviewer — Can view and leave review comments</option>
                  <option value="content_editor">Content Editor — Can edit copy & media on authorized pages</option>
                  <option value="designer">Designer — Can edit layout, styles and components</option>
                  <option value="administrator">Administrator — Can manage sites and invite team members</option>
                  <option value="viewer">Viewer — Read-only access</option>
                </select>
              </label>
              <div class="modal-actions" style="margin-top: 20px;">
                <button type="button" class="ghost-btn" data-close>Cancel</button>
                <button type="submit" class="primary-btn">Create invitation</button>
              </div>
            </form>
          </section>
        `;
        document.body.append(backdrop);
        const close = () => {
          release();
          backdrop.remove();
        };
        const release = bindDialog(backdrop, close);
        backdrop.querySelectorAll('[data-close]').forEach((btn) => btn.addEventListener('click', close));

        const form = backdrop.querySelector<HTMLFormElement>('[data-invite-form]');
        const emailInput = backdrop.querySelector<HTMLInputElement>('[data-invite-email]');
        const errorEl = backdrop.querySelector<HTMLElement>('[data-invite-error]');
        const roleSelect = backdrop.querySelector<HTMLSelectElement>('[data-invite-role]');
        const submitBtn = form?.querySelector<HTMLButtonElement>('button[type=submit]');

        form?.addEventListener('submit', async (e) => {
          e.preventDefault();
          const email = emailInput?.value.trim().toLowerCase() ?? '';
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!email || !emailRegex.test(email)) {
            if (errorEl) {
              errorEl.textContent = 'Please enter a valid email address.';
              errorEl.style.display = 'block';
            }
            emailInput?.focus();
            return;
          }
          if (errorEl) errorEl.style.display = 'none';

          const role = roleSelect?.value || 'reviewer';
          if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.textContent = 'Creating...';
          }

          try {
            const invite = await cloud.inviteMember(selectedWorkspaceId, email, role);
            close();
            say('Invitation created.');
            void renderTeam();
            openRevealLinkModal(invite.inviteUrl ?? '', email, role);
          } catch (err) {
            if (submitBtn) {
              submitBtn.disabled = false;
              submitBtn.textContent = 'Create invitation';
            }
            if (errorEl) {
              errorEl.textContent = err instanceof Error ? err.message : 'Could not create invitation.';
              errorEl.style.display = 'block';
            }
          }
        });
      };

      const openEditInviteModal = (inviteId: string, email: string, currentRole: string) => {
        const backdrop = document.createElement('div');
        backdrop.className = 'modal-backdrop';
        backdrop.innerHTML = `
          <section class="setup-modal edit-invite-modal" role="dialog" aria-modal="true" aria-labelledby="edit-invite-title">
            <div class="modal-head">
              <h2 id="edit-invite-title">Edit invitation role</h2>
              <button type="button" class="mini-btn" data-close aria-label="Close dialog">×</button>
            </div>
            <p class="modal-copy">Update the role assigned to <strong>${esc(email)}</strong> when they accept.</p>
            <form data-edit-invite-form>
              <label class="modal-field-label">
                Role
                <select data-edit-role required style="width: 100%; height: 38px; background: #0d1012; border: 1px solid var(--line); border-radius: 7px; color: #fff; padding: 0 10px; margin-top: 6px; box-sizing: border-box;">
                  ${['reviewer', 'content_editor', 'designer', 'administrator', 'viewer']
                    .map((r) => `<option value="${r}" ${r === currentRole ? 'selected' : ''}>${r.replace('_', ' ')}</option>`)
                    .join('')}
                </select>
              </label>
              <div class="modal-actions" style="margin-top: 20px;">
                <button type="button" class="ghost-btn" data-close>Cancel</button>
                <button type="submit" class="primary-btn">Save role</button>
              </div>
            </form>
          </section>
        `;
        document.body.append(backdrop);
        const close = () => {
          release();
          backdrop.remove();
        };
        const release = bindDialog(backdrop, close);
        backdrop.querySelectorAll('[data-close]').forEach((btn) => btn.addEventListener('click', close));

        const form = backdrop.querySelector<HTMLFormElement>('[data-edit-invite-form]');
        form?.addEventListener('submit', async (e) => {
          e.preventDefault();
          const newRole = backdrop.querySelector<HTMLSelectElement>('[data-edit-role]')?.value;
          if (!newRole) return;
          try {
            await cloud.updateInvitationRole(selectedWorkspaceId, inviteId, newRole);
            say('Invitation role updated.');
            close();
            void renderTeam();
          } catch (err) {
            say(err instanceof Error ? err.message : 'Could not update role.');
          }
        });
      };

      document.querySelector('[data-invite-member]')?.addEventListener('click', () => {
        openInviteModal();
      });

      document.querySelectorAll<HTMLSelectElement>('[data-member-role]').forEach((select) =>
        select.addEventListener('change', async () => {
          try {
            await cloud.updateMemberRole(
              selectedWorkspaceId,
              select.dataset.memberRole ?? '',
              select.value,
            );
            say('Role updated.');
          } catch (error) {
            say(error instanceof Error ? error.message : 'Could not update role.');
            void renderTeam();
          }
        }),
      );

      document.querySelectorAll<HTMLButtonElement>('[data-transfer-ownership]').forEach((button) =>
        button.addEventListener('click', async () => {
          const targetMembershipId = button.dataset.transferOwnership;
          const targetName = button.dataset.memberName || 'this member';
          if (!targetMembershipId) return;

          const confirmed = await askConfirm(
            `Transfer workspace ownership to ${targetName}? You will become an administrator and ${targetName} will become the new workspace owner.`,
          );
          if (!confirmed) return;

          try {
            await cloud.transferWorkspaceOwnership(selectedWorkspaceId, targetMembershipId);
            say(`Ownership successfully transferred to ${targetName}.`);
            if (selectedWorkspace) {
              selectedWorkspace.role = 'administrator';
            }
            void renderTeam();
          } catch (error) {
            say(error instanceof Error ? error.message : 'Could not transfer ownership.');
          }
        }),
      );

      document.querySelectorAll<HTMLButtonElement>('[data-remove-member]').forEach((button) =>
        button.addEventListener('click', async () => {
          if (!(await askConfirm('Remove this member?'))) return;
          try {
            await cloud.removeMember(selectedWorkspaceId, button.dataset.removeMember ?? '');
            say('Member removed.');
            void renderTeam();
          } catch (error) {
            say(error instanceof Error ? error.message : 'Could not remove member.');
          }
        }),
      );

      // Copy invite button with automatic link recovery
      document.querySelectorAll<HTMLButtonElement>('[data-copy-invite]').forEach((button) =>
        button.addEventListener('click', async () => {
          let url = button.dataset.inviteUrl;
          const inviteId = button.dataset.copyInvite;
          if (!url && inviteId) {
            try {
              const res = await cloud.revealInvitationLink(selectedWorkspaceId, inviteId);
              url = res.inviteUrl;
              button.dataset.inviteUrl = url;
            } catch (err) {
              say(err instanceof Error ? err.message : 'Could not retrieve link.');
              return;
            }
          }
          if (url) {
            const copied = await copyTextWithFallback(url);
            if (copied) {
              say('Invitation link copied.');
            } else {
              openRevealLinkModal(url, 'Collaborator', 'member');
            }
          }
        }),
      );

      // More actions menu toggle
      document.querySelectorAll<HTMLButtonElement>('[data-invite-menu]').forEach((button) =>
        button.addEventListener('click', (e) => {
          e.stopPropagation();
          const inviteId = button.dataset.inviteMenu;
          const menu = document.querySelector<HTMLElement>(`#inviteMenu_${inviteId}`);
          if (!menu) return;
          const isHidden = menu.hidden;
          document.querySelectorAll<HTMLElement>('.invite-dropdown-menu').forEach((m) => (m.hidden = true));
          menu.hidden = !isHidden;
        }),
      );

      // Close dropdown menus on outside click
      document.addEventListener(
        'click',
        (e) => {
          if (!(e.target as HTMLElement)?.closest('.invite-menu-wrap')) {
            document.querySelectorAll<HTMLElement>('.invite-dropdown-menu').forEach((m) => (m.hidden = true));
          }
        },
        { capture: true },
      );

      // Reveal link menu action
      document.querySelectorAll<HTMLButtonElement>('[data-reveal-invite]').forEach((button) =>
        button.addEventListener('click', async () => {
          document.querySelectorAll<HTMLElement>('.invite-dropdown-menu').forEach((m) => (m.hidden = true));
          const inviteId = button.dataset.revealInvite;
          const email = button.dataset.inviteEmail || 'Collaborator';
          const role = button.dataset.inviteRole || 'reviewer';
          let url = button.dataset.inviteUrl;
          if (!url && inviteId) {
            try {
              const res = await cloud.revealInvitationLink(selectedWorkspaceId, inviteId);
              url = res.inviteUrl;
              button.dataset.inviteUrl = url;
            } catch (err) {
              say(err instanceof Error ? err.message : 'Could not retrieve link.');
              return;
            }
          }
          if (url) openRevealLinkModal(url, email, role);
        }),
      );

      // Edit invite role menu action
      document.querySelectorAll<HTMLButtonElement>('[data-edit-invite]').forEach((button) =>
        button.addEventListener('click', () => {
          document.querySelectorAll<HTMLElement>('.invite-dropdown-menu').forEach((m) => (m.hidden = true));
          const inviteId = button.dataset.editInvite;
          const email = button.dataset.inviteEmail || '';
          const role = button.dataset.currentRole || 'reviewer';
          if (inviteId) openEditInviteModal(inviteId, email, role);
        }),
      );

      // Cancel invite menu action
      document.querySelectorAll<HTMLButtonElement>('[data-cancel-invite]').forEach((button) =>
        button.addEventListener('click', async () => {
          document.querySelectorAll<HTMLElement>('.invite-dropdown-menu').forEach((m) => (m.hidden = true));
          const inviteId = button.dataset.cancelInvite;
          const email = button.dataset.inviteEmail || 'this collaborator';
          if (!inviteId) return;
          if (!(await askConfirm(`Cancel pending invitation for ${email}?`))) return;
          try {
            await cloud.cancelInvitation(selectedWorkspaceId, inviteId);
            say('Invitation canceled.');
            void renderTeam();
          } catch (err) {
            say(err instanceof Error ? err.message : 'Could not cancel invitation.');
          }
        }),
      );
    } catch (error) {
      if (version === renderVersion) view.innerHTML = errorState(error, 'team');
    }
  };
  const renderUnavailable = (section: Section) => {
    if (section === 'account') {
      view.innerHTML = `<div class="customer-heading"><div><p class="eyebrow">Your account</p><h1>Account settings</h1><p>Your WebKiln profile and session.</p></div></div><section class="dashboard-panel"><h2>${esc(session.user.displayName)}</h2><p>${esc(session.user.email)}</p><button type="button" class="ghost-btn" data-account-signout>Sign out</button></section>`;
      view
        .querySelector('[data-account-signout]')
        ?.addEventListener('click', () => void signOut(cloud));
      return;
    }
    const details: Record<string, [string, string]> = {
      domains: [
        'Domains',
        'Connect a custom domain when you are ready. Domain provisioning is intentionally kept behind the approved provider boundary.',
      ],
      forms: [
        'Form submissions',
        'Review stored submissions from your published forms. File uploads and email delivery remain intentionally unavailable.',
      ],
      assets: [
        'Asset library',
        'Asset metadata is available from the Assets section. Binary storage remains intentionally unconfigured.',
      ],
      billing: [
        'Billing',
        'Billing is disabled. WebKiln’s current testing environment does not collect payments or request a payment method.',
      ],
      team: [
        'Team members',
        'Manage workspace roles, protected invitations, and member access from the collaboration controls.',
      ],
      account: [
        'Account settings',
        `Signed in as ${session.user.email}. Use the account menu to sign out.`,
      ],
    };
    const [title, description] = details[section];
    view.innerHTML = `<div class="customer-heading"><div><p class="eyebrow">Workspace / ${title}</p><h1>${title}</h1><p>${description}</p></div></div><section class="dashboard-panel unavailable-panel"><span class="status-mark">—</span><h2>Provider boundary</h2><p>This area is visible so the product surface is clear, but no action is presented as complete until its supporting service is available.</p></section>`;
    view.querySelector('[data-signout]')?.addEventListener('click', () => void signOut(cloud));
  };
  const siteCard = (site: CloudSite) => {
    const isArchived = site.status === 'archived';
    const publicUrl = `${window.location.origin}/sites/${encodeURIComponent(site.slug)}`;
    const statusLabel = isArchived
      ? 'ARCHIVED'
      : site.published
        ? site.visibility === 'private'
          ? 'PRIVATE'
          : 'PUBLIC'
        : 'DRAFT';
    return `<article class="website-card ${isArchived ? 'is-archived' : ''}"><div class="website-thumbnail"><iframe src="/api/sites/${encodeURIComponent(site.id)}/thumbnail" title="${esc(site.name)} preview" sandbox="" loading="lazy" tabindex="-1"></iframe><small class="${isArchived ? 'status-archived' : ''}">${statusLabel}</small></div><div class="website-card-body"><div class="website-card-head"><div><h2>${esc(site.name)}</h2><p class="website-url">${site.customDomain ? esc(site.customDomain) : publicUrl}</p></div><details class="site-actions"><summary aria-label="Actions for ${esc(site.name)}">•••</summary><div class="site-actions-menu"><button type="button" data-action="rename" data-site="${site.id}">Rename</button><button type="button" data-action="duplicate" data-site="${site.id}">Duplicate</button><button type="button" data-action="export" data-site="${site.id}">Export static site</button><button type="button" data-action="backup" data-site="${site.id}">Download backup</button><button type="button" data-action="domain" data-site="${site.id}">Connect domain</button><button type="button" data-action="archive" data-site="${site.id}">${isArchived ? 'Restore website' : 'Archive website'}</button>${isArchived ? `<button type="button" data-action="delete" data-site="${site.id}" class="danger-action">Delete permanently</button>` : ''}</div></details></div><div class="website-meta"><span class="status-chip ${isArchived ? 'archived' : site.published ? 'published' : ''}">${isArchived ? 'Archived' : site.published ? (site.visibility === 'private' ? 'Private' : 'Public') : 'Unpublished'}</span><span>Edited ${new Date(site.updatedAt).toLocaleDateString()}</span><span>${site.pageCount ?? 0} ${(site.pageCount ?? 0) === 1 ? 'page' : 'pages'}</span><span>${site.publishedAt ? `Published ${new Date(site.publishedAt).toLocaleString()}` : 'Never published'}</span><span class="deployment-status">${isArchived ? 'Archived' : site.published ? (site.publishedRevision === site.currentRevision ? 'Live · up to date' : 'Unpublished changes') : 'Not deployed'}</span></div><div class="website-actions">${isArchived ? `<button class="primary-btn" data-action="archive" data-site="${site.id}">Restore website</button><button class="danger-btn" data-action="delete" data-site="${site.id}">Delete permanently</button>` : `<button class="primary-btn" data-action="edit" data-site="${site.id}">Open editor</button><button class="ghost-btn" data-action="preview" data-site="${site.id}">Preview draft</button>${site.published ? `<a class="text-button" href="${publicUrl}" target="_blank" rel="noreferrer">View published</a>` : ''}`}</div></div></article>`;
  };
  const thumbnailObserver = new ResizeObserver((entries) => {
    entries.forEach((entry) => {
      const frame = entry.target.querySelector('iframe');
      if (frame) frame.style.transform = `scale(${entry.contentRect.width / 1200})`;
    });
  });
  const handleCreateBlankSite = async () => {
    if (!selectedWorkspaceId) return;
    const name = await askText('Website name', 'Untitled site');
    if (name === null || !name.trim()) return;
    try {
      const site = await cloud.createSite(selectedWorkspaceId, name.trim(), { blank: true });
      const blankTemplate = templateById('blank');
      const project = createTemplateProject(blankTemplate, site.id, name.trim(), {
        description: '',
        businessType: 'blank',
        goal: 'custom',
        selectedPageSlugs: ['/'],
        style: 'minimal',
      });
      await cloud.saveProject(site.id, {
        project,
        expectedRevision: site.currentRevision,
      });
      say('Blank website created.');
      navigate(`/editor/${encodeURIComponent(site.id)}`);
    } catch (error) {
      say(error instanceof Error ? error.message : 'Could not create blank website.');
    }
  };
  const bindCommon = () => {
    thumbnailObserver.disconnect();
    view
      .querySelectorAll('.website-thumbnail')
      .forEach((preview) => thumbnailObserver.observe(preview));
    view.querySelectorAll<HTMLDetailsElement>('.site-actions').forEach((details) => {
      details.ontoggle = () => {
        const card = details.closest('.website-card');
        if (card) card.classList.toggle('menu-open', details.open);
        if (details.open) {
          view.querySelectorAll<HTMLDetailsElement>('.site-actions[open]').forEach((other) => {
            if (other !== details) {
              other.open = false;
              other.closest('.website-card')?.classList.remove('menu-open');
            }
          });
        }
      };
    });
    view.querySelectorAll<HTMLButtonElement>('[data-action]').forEach((button) => {
      button.onclick = (event) => {
        event.stopPropagation();
        const details = button.closest<HTMLDetailsElement>('.site-actions');
        if (details) {
          details.open = false;
          details.closest('.website-card')?.classList.remove('menu-open');
        }
        void handleSiteAction(button.dataset.action ?? '', button.dataset.site ?? '');
      };
    });
    view
      .querySelectorAll<HTMLButtonElement>('[data-create-blank-site]')
      .forEach((button) => (button.onclick = () => void handleCreateBlankSite()));
    view
      .querySelectorAll('[data-create-site]')
      .forEach((button) => button.setAttribute('data-onboarding-link', 'true'));
  };
  const handleSiteAction = async (action: string, siteId: string) => {
    const site = sites.find((item) => item.id === siteId);
    if (!site) return;
    try {
      if (action === 'edit' || action === 'preview')
        navigate(
          `/editor/${encodeURIComponent(siteId)}${action === 'preview' ? '?preview=1' : ''}`,
        );
      else if (action === 'export' || action === 'backup') {
        const remote = await cloud.getProject(siteId);
        if (action === 'backup')
          downloadFile(
            `${site.slug}-webkiln-backup.json`,
            JSON.stringify(createProjectBackup(remote.project), null, 2),
            'application/json',
          );
        else
          createStaticExport(remote.project).forEach((file) =>
            downloadFile(
              file.path.replace(/\//g, '-'),
              file.content,
              file.contentType === 'css'
                ? 'text/css'
                : file.contentType === 'html'
                  ? 'text/html'
                  : 'application/octet-stream',
            ),
          );
        say(action === 'backup' ? 'Backup downloaded.' : 'Static export downloaded.');
      } else if (action === 'rename') {
        const name = await askText('New website name', site.name);
        if (name?.trim()) {
          await cloud.updateSite(siteId, { name: name.trim() });
          say('Website renamed.');
          await loadSites();
        }
      } else if (action === 'duplicate') {
        const name = await askText('Name for duplicated website', `${site.name} copy`);
        if (name === null) return;
        const copy = await cloud.duplicateSite(siteId, name.trim() || undefined);
        say('Website duplicated.');
        await loadSites();
        navigate(`/editor/${encodeURIComponent(copy.id)}`);
      } else if (action === 'archive') {
        const next = site.status === 'archived' ? 'active' : 'archived';
        if (
          next === 'archived' &&
          !(await askConfirm(`Archive "${site.name}"? It will be moved to archived websites.`))
        )
          return;
        await cloud.updateSite(siteId, { status: next });
        say(next === 'archived' ? 'Website archived.' : 'Website restored.');
        await loadSites();
      } else if (action === 'delete') {
        if (site.status !== 'archived') {
          say('Archive the website before deleting it.');
          return;
        }
        const confirmed = await askConfirm(
          `Permanently delete "${site.name}"? This action cannot be undone. All pages, forms, and revision history will be permanently destroyed.`,
        );
        if (!confirmed) return;
        const confirmationText = await askText(
          `Type "${site.name}" to confirm permanent deletion:`,
          '',
        );
        if (confirmationText !== site.name) {
          say('Deletion cancelled: website name did not match.');
          return;
        }
        await cloud.deleteSite(siteId);
        say('Website permanently deleted.');
        await loadSites();
      } else if (action === 'domain') {
        const domain = await askText(
          'Custom domain (DNS setup is still required)',
          site.customDomain ?? '',
        );
        if (domain?.trim()) {
          await cloud.updateSite(siteId, { customDomain: domain.trim() });
          say('Domain saved. DNS connection remains pending.');
          await loadSites();
        }
      }
    } catch (error) {
      say(error instanceof Error ? error.message : 'Website action failed.');
    }
  };
  const downloadFile = (name: string, content: string, type: string) => {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([content], { type }));
    link.download = name;
    link.click();
    URL.revokeObjectURL(link.href);
  };
  const empty = (title: string, copy: string) => {
    const actions: Record<string, [string, string]> = {
      'No websites yet': ['Create blank website', 'data-create-blank-site'],
      'No matching websites': ['Clear search and filters', 'data-clear-website-filters'],
      'No archived websites': ['View active websites', 'data-view-active-websites'],
      'No collections yet': ['Create a collection', 'data-create-collection'],
      'No automations yet': ['Create a draft automation', 'data-create-automation'],
      'No records': ['Create the first record', 'data-new-record'],
      'No asset metadata yet': sites.length
        ? ['Register an asset', 'data-register-asset']
        : ['Create blank website', 'data-create-blank-site'],
      'No forms yet': ['Create a form', 'data-create-form'],
    };
    const action = actions[title];
    return `<div class="empty-state dashboard-empty"><span class="empty-state-icon" aria-hidden="true">✦</span><strong>${esc(title)}</strong><small>${esc(copy)}</small>${action ? `<button class="primary-btn" type="button" data-empty-trigger="${action[1]}">${action[0]}</button>` : ''}</div>`;
  };
  const loading = (text: string) => `<div class="cloud-loading">${text}…</div>`;
  const errorState = (error: unknown, section: Section) =>
    `<div class="error-state">${esc(error instanceof Error ? error.message : 'Could not load this area.')} <button type="button" data-nav="${section}">Retry</button></div>`;
  const formatBytes = (bytes: number) =>
    bytes < 1024
      ? `${bytes} B`
      : bytes < 1024 ** 2
        ? `${(bytes / 1024).toFixed(1)} KB`
        : `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  const signOut = async (client: WebKilnApiClient) => {
    await client.signOut().catch(() => undefined);
    navigate('/login');
  };
  document.querySelector('.customer-dashboard')?.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest<HTMLElement>(
      '[data-nav], [data-create-site], [data-create-blank-site], [data-empty-trigger]',
    );
    if (!button) return;
    if (button.hasAttribute('data-empty-trigger')) {
      const trigger = button.dataset.emptyTrigger;
      if (trigger === 'data-create-site') {
        navigate(`/onboarding?workspace=${encodeURIComponent(selectedWorkspaceId ?? '')}`);
        return;
      }
      if (trigger === 'data-create-blank-site') {
        void handleCreateBlankSite();
        return;
      }
      if (trigger === 'data-view-active-websites') {
        const filter = view.querySelector<HTMLSelectElement>('[data-website-filter]');
        if (filter) {
          filter.value = 'all';
          filter.dispatchEvent(new Event('change'));
        }
        return;
      }
      if (trigger === 'data-clear-website-filters') {
        const search = view.querySelector<HTMLInputElement>('[data-website-search]');
        const filter = view.querySelector<HTMLSelectElement>('[data-website-filter]');
        if (search) search.value = '';
        if (filter) filter.value = 'all';
        search?.dispatchEvent(new Event('input'));
        return;
      }
      view.querySelector<HTMLButtonElement>(`[${trigger}]`)?.click();
      return;
    }
    if (button.hasAttribute('data-create-blank-site')) {
      void handleCreateBlankSite();
      return;
    }
    if (button.hasAttribute('data-create-site')) {
      const query = new URLSearchParams();
      if (selectedWorkspaceId) query.set('workspace', selectedWorkspaceId);
      navigate(`/onboarding?${query}`);
    } else if (sections.includes(button.dataset.nav as Section))
      renderSection(button.dataset.nav as Section);
  });
  document.addEventListener('click', (event) => {
    if (!(event.target as HTMLElement).closest('.site-actions')) {
      view.querySelectorAll<HTMLDetailsElement>('.site-actions[open]').forEach((details) => {
        details.open = false;
        details.closest('.website-card')?.classList.remove('menu-open');
      });
    }
  });
  window.addEventListener('popstate', async () => {
    const route = parseRoute();
    if (route.kind !== 'dashboard') return;
    const workspaceId = route.workspaceId ?? workspaces[0]?.id ?? '';
    const version = ++renderVersion;
    if (workspaceId !== selectedWorkspaceId) {
      selectedWorkspaceId = workspaceId;
      sites = workspaceId ? await cloud.listSites(workspaceId).catch(() => []) : [];
      if (version !== renderVersion) return;
    }
    renderSection(
      sections.includes(route.section as Section) ? (route.section as Section) : 'overview',
      false,
    );
  });
  document
    .querySelector('[data-local-editor]')
    ?.addEventListener('click', () => navigate('/local'));
  document.querySelector('[data-signout]')?.addEventListener('click', () => void signOut(cloud));
  view.innerHTML = loading('Loading workspace');
  try {
    workspaces = await cloud.listWorkspaces();
    selectedWorkspaceId = workspaces.some((workspace) => workspace.id === initialWorkspaceId)
      ? initialWorkspaceId
      : (workspaces[0]?.id ?? '');
    if (selectedWorkspaceId) sites = await cloud.listSites(selectedWorkspaceId);
    renderSection(
      (sections.includes(initialSection as Section) ? initialSection : 'overview') as Section,
      false,
    );
  } catch (error) {
    view.innerHTML = errorState(error, 'overview');
  }
  try {
    const local = storage.load();
    const preview = previewLocalProject(JSON.stringify(local));
    if (preview.summary.pageCount > 0 && selectedWorkspaceId) {
      const card = document.createElement('div');
      card.className = 'migration-card';
      card.innerHTML = `<div><p class="eyebrow">Local project found</p><strong>${esc(local.site.title)}</strong><small>${preview.summary.pageCount} pages · import keeps a local backup</small></div><button class="ghost-btn" type="button">Import local project</button>`;
      card.querySelector('button')?.addEventListener('click', async () => {
        if (!(await askConfirm('Import this project as a new cloud website?'))) return;
        createMigrationBackup(preview.project);
        const site = await cloud.createSite(
          selectedWorkspaceId,
          preview.project.site.title || 'Imported website',
        );
        await cloud.saveProject(site.id, {
          project: preview.project,
          expectedRevision: site.currentRevision,
        });
        navigate(`/editor/${encodeURIComponent(site.id)}`);
      });
      view.append(card);
    }
  } catch (err) {
    void err;
  }
}
